import React, { useState, useEffect, useRef, useCallback } from "react";

// ── API Layer (all calls go through server.js on same origin) ─────────────────
async function apiPost(endpoint, body, token = null) {
  const res = await fetch("/api/proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint, body, token }),
  });
  return res.json();
}
const logout         = (uid, token)              => apiPost("/Logout",       { uid }, token);
const getUserDetails = (uid, token)              => apiPost("/UserDetails",  { uid }, token);
const getOrderBook   = (uid, token)              => apiPost("/OrderBook",    { uid }, token);
const getTradeBook   = (uid, actid, token)       => apiPost("/TradeBook",    { uid, actid }, token);
const getPositionBook= (uid, actid, token)       => apiPost("/PositionBook", { uid, actid }, token);
const getHoldings    = (uid, actid, prd, token)  => apiPost("/Holdings",     { uid, actid, prd }, token);
const getLimits      = (uid, actid, token)       => apiPost("/Limits",       { uid, actid }, token);
const placeOrder     = (params, token)           => apiPost("/PlaceOrder",   params, token);
const cancelOrder    = (uid, norenordno, token)  => apiPost("/CancelOrder",  { uid, norenordno }, token);
const searchScrip    = (uid, stext, token, exch) => apiPost("/SearchScrip",  exch ? { uid, stext, exch } : { uid, stext }, token);
const modifyOrder    = (params, token)           => apiPost("/ModifyOrder",  params, token);

// ── LocalStorage helpers ──────────────────────────────────────────────────────
const SK_MASTERS  = "kambala_ct_masters";
const SK_CHILDREN = "kambala_ct_children";
const SK_RULES    = "kambala_ct_rules";
const persist = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch(e){} };
const restore = (k, d) => { try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(d)); } catch(e){ return d; } };

// ── Theme ─────────────────────────────────────────────────────────────────────
const C = {
  bg:"#0A0D14", surface:"#111520", panel:"#151C2C", border:"#1E2A40", borderHi:"#2A3A58",
  accent:"#00C8FF", green:"#00E676", red:"#FF4444", yellow:"#FFB700",
  text:"#E0E8F5", textDim:"#7A8BA8", textMuted:"#4A5A72",
};

// ── Style helpers ─────────────────────────────────────────────────────────────
const btn = (v="default") => ({
  padding:"8px 16px", borderRadius:6, border:"none", cursor:"pointer",
  fontSize:13, fontWeight:600, transition:"all 0.15s",
  ...(v==="primary" ? {background:C.accent,color:"#000"}
    : v==="green"   ? {background:C.green,color:"#000"}
    : v==="danger"  ? {background:`${C.red}22`,color:C.red,border:`1px solid ${C.red}44`}
    :                 {background:C.border,color:C.text}),
});
const inp = {background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,padding:"9px 13px",fontSize:13,width:"100%",outline:"none",boxSizing:"border-box"};
const lbl = {fontSize:11,color:C.textDim,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:5,display:"block"};
const th  = {padding:"10px 14px",fontSize:11,color:C.textDim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.6px",textAlign:"left",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"};
const td  = {padding:"10px 14px",fontSize:13,color:C.text,borderBottom:`1px solid ${C.border}20`};
const badge = color => ({display:"inline-block",padding:"2px 8px",borderRadius:4,fontSize:11,fontWeight:700,color,border:`1px solid ${color}40`,background:`${color}18`});
const fmt = v => `₹${parseFloat(v||0).toLocaleString("en-IN",{minimumFractionDigits:2})}`;
const pnlColor = v => parseFloat(v)>=0 ? C.green : C.red;

const PRODUCTS = [
  {prd:"I",alias:"MIS",  label:"MIS — Intraday",       exch:"NSE,BSE,NFO,BFO"},
  {prd:"C",alias:"CNC",  label:"CNC — Delivery",        exch:"NSE,BSE"},
  {prd:"M",alias:"NRML", label:"NRML — Margin/Normal",  exch:"NFO,BFO,MCX,CDS"},
  {prd:"H",alias:"CO",   label:"CO — Cover Order (H)",  exch:"NSE,BSE,NFO,BFO"},
  {prd:"B",alias:"BO",   label:"BO — Bracket Order",    exch:"NSE,BSE,NFO,BFO"},
  {prd:"F",alias:"MTF",  label:"MTF — Margin Trade",    exch:"NSE,BSE"},
];
const prdAlias = p => PRODUCTS.find(x=>x.prd===p)?.alias || p;

// ── Shared Components ─────────────────────────────────────────────────────────
function StatusPill({status}) {
  const map={OPEN:C.accent,COMPLETE:C.green,CANCELED:C.textDim,REJECTED:C.red,PENDING:C.yellow,TRIGGER_PENDING:C.yellow};
  const color=map[status]||C.textDim;
  return <span style={badge(color)}>{status||"—"}</span>;
}
function LoadingRow({cols}) { return <tr><td colSpan={cols} style={{...td,textAlign:"center",color:C.textMuted,padding:28}}>Loading…</td></tr>; }
function EmptyRow({cols,msg}) { return <tr><td colSpan={cols} style={{...td,textAlign:"center",color:C.textMuted,padding:28}}>{msg||"No data"}</td></tr>; }
function Logo({size=36}) {
  return (
    <div style={{width:size,height:size,borderRadius:size*0.22,background:C.accent,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
      <svg width={size*0.55} height={size*0.55} viewBox="0 0 20 20" fill="none">
        <polyline points="2,14 7,8 11,12 18,4" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

// ── Credentials Page ──────────────────────────────────────────────────────────
function CredentialsPage({onNext}) {
  const [form,setForm]=useState({clientId:"",secretKey:"",password:"",otp:""});
  const [showSec,setShowSec]=useState(false);
  const [showPas,setShowPas]=useState(false);
  const [err,setErr]=useState("");
  const set=k=>e=>setForm(f=>({...f,[k]:e.target.value}));
  function go(){
    if(!form.clientId.trim()){setErr("Client ID is required.");return;}
    if(!form.secretKey.trim()){setErr("Secret Key is required.");return;}
    onNext(form.clientId.trim(),form.secretKey.trim());
  }
  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Inter',sans-serif",padding:20}}>
      <div style={{width:"100%",maxWidth:440}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:28}}><Logo size={42}/><div><div style={{fontWeight:800,fontSize:20,color:C.text}}>Kambala Copy Trader</div><div style={{fontSize:12,color:C.textDim}}>Step 1 — Enter API Credentials</div></div></div>
        <div style={{background:C.panel,border:`1px solid ${C.borderHi}`,borderRadius:16,padding:26}}>
          <div style={{marginBottom:14}}><label style={lbl}>Client ID *</label><input style={inp} placeholder="e.g. 92CHV00017_U" value={form.clientId} onChange={set("clientId")}/></div>
          <div style={{marginBottom:14}}>
            <label style={lbl}>Secret Key *</label>
            <div style={{position:"relative"}}>
              <input style={{...inp,paddingRight:52}} type={showSec?"text":"password"} placeholder="Your API secret key" value={form.secretKey} onChange={set("secretKey")}/>
              <button onClick={()=>setShowSec(v=>!v)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.textDim,cursor:"pointer",fontSize:12}}>{showSec?"Hide":"Show"}</button>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,margin:"16px 0",color:C.textMuted,fontSize:11}}><div style={{flex:1,height:1,background:C.border}}/> OPTIONAL <div style={{flex:1,height:1,background:C.border}}/></div>
          <div style={{marginBottom:14}}>
            <label style={lbl}>Password</label>
            <div style={{position:"relative"}}>
              <input style={{...inp,paddingRight:52}} type={showPas?"text":"password"} placeholder="Kambala password" value={form.password} onChange={set("password")}/>
              <button onClick={()=>setShowPas(v=>!v)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.textDim,cursor:"pointer",fontSize:12}}>{showPas?"Hide":"Show"}</button>
            </div>
          </div>
          <div style={{marginBottom:20}}><label style={lbl}>OTP / TOTP</label><input style={{...inp,textAlign:"center",letterSpacing:"0.2em",fontSize:18}} placeholder="123456" value={form.otp} onChange={set("otp")} maxLength={8} inputMode="numeric"/></div>
          {err&&<div style={{background:`${C.red}15`,border:`1px solid ${C.red}44`,borderRadius:6,padding:"8px 12px",fontSize:12,color:C.red,marginBottom:12}}>{err}</div>}
          <button style={{...btn("primary"),width:"100%",padding:"12px",fontSize:15,fontWeight:700}} onClick={go}>Continue to Login →</button>
          <div style={{fontSize:11,color:C.textMuted,textAlign:"center",marginTop:10}}>🔒 Credentials only sent to trade.iseindia.com</div>
        </div>
      </div>
    </div>
  );
}

// ── Proxy Status ──────────────────────────────────────────────────────────────
function ProxyStatus() {
  const [s,setS]=useState("checking");
  useEffect(()=>{
    fetch("/api/health").then(r=>r.json()).then(d=>setS(d.status==="ok"?"ok":"error")).catch(()=>setS("error"));
  },[]);
  const cfg={checking:{bg:`${C.yellow}15`,border:`${C.yellow}44`,color:C.yellow,icon:"⏳",text:"Checking server…"},ok:{bg:`${C.green}15`,border:`${C.green}44`,color:C.green,icon:"✅",text:"Server running — ready to login!"},error:{bg:`${C.red}15`,border:`${C.red}44`,color:C.red,icon:"❌",text:"Server not found. Run: node server.js"}};
  const c=cfg[s];
  return <div style={{background:c.bg,border:`1px solid ${c.border}`,borderRadius:8,padding:"9px 14px",marginTop:12,fontSize:12,color:c.color,display:"flex",alignItems:"center",gap:8}}><span>{c.icon}</span><span>{c.text}</span></div>;
}

// ── Login Page ────────────────────────────────────────────────────────────────
function LoginPage({clientId,secretKey,onAuth,onBack}) {
  const [tab,setTab]=useState("code");
  const [code,setCode]=useState("");
  const [token,setToken]=useState("");
  const [uid,setUid]=useState("");
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");
  const loginUrl=`https://trade.iseindia.com/OAuthlogin/authorize/oauth?client_id=${clientId}_U`;

  async function handleCode() {
    const c=code.trim();
    if(!c){setErr("Paste the auth code.");return;}
    setLoading(true);setErr("");
    try{
      const res=await fetch("/api/token",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({code:c,client_id:clientId.trim(),secret_key:secretKey.trim()})});
      const data=await res.json();
      if(data.stat==="Ok"&&data.access_token){
        onAuth(data.access_token,{...data,uid:data.uid||clientId,actid:data.actid||data.uid||clientId});
      } else {setErr(data.emsg||"Token exchange failed. Get a fresh code and try again.");}
    }catch(e){setErr("Server error: "+e.message);}
    setLoading(false);
  }

  function handleToken() {
    const t=token.trim(),u=uid.trim().toUpperCase();
    if(!t){setErr("Paste your access token.");return;}
    if(!u){setErr("Enter your User ID.");return;}
    onAuth(t,{uid:u,actid:u,access_token:t,stat:"Ok"});
  }

  const tabStyle=active=>({flex:1,padding:"9px",border:"none",cursor:"pointer",fontSize:13,fontWeight:600,borderRadius:8,background:active?C.accent:"transparent",color:active?"#000":C.textDim});
  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Inter',sans-serif",padding:20}}>
      <div style={{width:"100%",maxWidth:460}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:24}}><Logo size={42}/><div><div style={{fontWeight:800,fontSize:20,color:C.text}}>Kambala Copy Trader</div><div style={{fontSize:12,color:C.textDim}}>Step 2 — Authenticate</div></div></div>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:"9px 16px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:13,color:C.textDim}}>Client ID: <strong style={{color:C.accent}}>{clientId}</strong></span>
          <button style={{background:"none",border:"none",color:C.textDim,fontSize:12,cursor:"pointer",textDecoration:"underline"}} onClick={onBack}>Change</button>
        </div>
        <div style={{background:C.panel,border:`1px solid ${C.borderHi}`,borderRadius:16,padding:24}}>
          <div style={{display:"flex",gap:4,background:C.surface,borderRadius:10,padding:4,marginBottom:20}}>
            <button style={tabStyle(tab==="code")} onClick={()=>{setTab("code");setErr("");}}>🔄 Auto Login</button>
            <button style={tabStyle(tab==="token")} onClick={()=>{setTab("token");setErr("");}}>🔑 Paste Token</button>
          </div>
          {tab==="code"&&(
            <div>
              <div style={{marginBottom:14}}>
                <a href={loginUrl} target="_blank" rel="noreferrer" style={{...btn("primary"),display:"inline-flex",alignItems:"center",gap:6,textDecoration:"none",fontSize:13}}>Step 1 — Open Kambala Login ↗</a>
                <div style={{fontSize:11,color:C.textMuted,marginTop:8}}>Login then copy the <code style={{color:C.accent}}>code</code> from the redirect URL.</div>
              </div>
              <div style={{height:1,background:C.border,margin:"14px 0"}}/>
              <label style={lbl}>Step 2 — Paste Auth Code *</label>
              <input style={inp} placeholder="Paste code here…" value={code} onChange={e=>setCode(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleCode()}/>
              {err&&<div style={{background:`${C.red}15`,border:`1px solid ${C.red}44`,borderRadius:6,padding:"8px 12px",fontSize:12,color:C.red,marginTop:10}}>{err}</div>}
              <button style={{...btn("primary"),width:"100%",padding:"12px",marginTop:14,fontSize:14,fontWeight:700}} onClick={handleCode} disabled={loading}>
                {loading?"Exchanging token…":"🔄 Get Token & Login"}
              </button>
              <ProxyStatus/>
            </div>
          )}
          {tab==="token"&&(
            <div>
              <div style={{background:`${C.yellow}12`,border:`1px solid ${C.yellow}33`,borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12,color:C.yellow}}>⚠️ Tokens expire daily. Use Auto Login for a fresh token automatically.</div>
              <div style={{marginBottom:12}}><label style={lbl}>User ID *</label><input style={inp} placeholder="e.g. 92CHV00017_U" value={uid} onChange={e=>setUid(e.target.value.toUpperCase())}/></div>
              <div style={{marginBottom:16}}><label style={lbl}>Access Token *</label><textarea style={{...inp,height:90,resize:"vertical",fontFamily:"monospace",fontSize:12}} placeholder="Paste access_token…" value={token} onChange={e=>setToken(e.target.value)}/></div>
              {err&&<div style={{background:`${C.red}15`,border:`1px solid ${C.red}44`,borderRadius:6,padding:"8px 12px",fontSize:12,color:C.red,marginBottom:12}}>{err}</div>}
              <button style={{...btn("primary"),width:"100%",padding:"12px",fontSize:14,fontWeight:700}} onClick={handleToken}>Connect with Token</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Order Book ────────────────────────────────────────────────────────────────
function OrderBookTab({uid,token}) {
  const [orders,setOrders]       = useState(null);
  const [loading,setLoading]     = useState(false);
  const [cancelling,setCancelling] = useState({});
  const [selected,setSelected]   = useState(new Set());
  const [bulkLoading,setBulkLoading] = useState(false);
  const [bulkMsg,setBulkMsg]     = useState(null);
  const [filterStatus,setFilterStatus] = useState("ALL");
  const [modifyOrder_,setModifyOrder_] = useState(null); // order being modified

  async function load(){
    setLoading(true);
    const r=await getOrderBook(uid,token);
    setOrders(Array.isArray(r)?r:[]);
    setSelected(new Set());
    setLoading(false);
  }
  useEffect(()=>{load();},[]);

  // Single cancel
  async function cancel(norenordno){
    setCancelling(p=>({...p,[norenordno]:true}));
    await cancelOrder(uid,norenordno,token);
    await load();
    setCancelling(p=>({...p,[norenordno]:false}));
  }

  // Bulk cancel selected
  async function bulkCancel(){
    const ids=[...selected];
    if(!ids.length)return;
    if(!window.confirm(`Cancel ${ids.length} selected order${ids.length>1?"s":""}?`))return;
    setBulkLoading(true); setBulkMsg(null);
    let ok=0,fail=0;
    for(const id of ids){
      const r=await cancelOrder(uid,id,token);
      r.stat==="Ok"?ok++:fail++;
    }
    setBulkMsg({type:fail===0?"success":"warn",
      text:`Cancelled ${ok} order${ok!==1?"s":""}${fail?`, ${fail} failed`:""}.`});
    setBulkLoading(false);
    await load();
  }

  const cancellable = ["OPEN","PENDING","TRIGGER_PENDING"];
  const statuses    = ["ALL","OPEN","COMPLETE","REJECTED","CANCELED","PENDING","TRIGGER_PENDING"];

  const displayed = (orders||[]).filter(o=>filterStatus==="ALL"||o.status===filterStatus);
  const cancellableOrders = displayed.filter(o=>cancellable.includes(o.status));
  const allSelected = cancellableOrders.length>0 && cancellableOrders.every(o=>selected.has(o.norenordno));

  function toggleSelectAll(){
    if(allSelected){
      setSelected(s=>{const n=new Set(s);cancellableOrders.forEach(o=>n.delete(o.norenordno));return n;});
    } else {
      setSelected(s=>{const n=new Set(s);cancellableOrders.forEach(o=>n.add(o.norenordno));return n;});
    }
  }
  function toggleOne(id){
    setSelected(s=>{const n=new Set(s);n.has(id)?n.delete(id):n.add(id);return n;});
  }

  const cols=["","Time","Symbol","Exchange","Type","Qty","Price","Product","Status","Order No","Action"];

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:10}}>
        <div style={{fontSize:16,fontWeight:700,color:C.text}}>Order Book</div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          {/* Status filter */}
          <select style={{...inp,width:"auto",padding:"6px 12px",fontSize:12}} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
            {statuses.map(s=><option key={s}>{s}</option>)}
          </select>
          {/* Bulk cancel button */}
          {selected.size>0&&(
            <button style={{...btn("danger"),padding:"6px 14px",fontSize:12,fontWeight:700}}
              onClick={bulkCancel} disabled={bulkLoading}>
              {bulkLoading?`⏳ Cancelling…`:`✕ Cancel Selected (${selected.size})`}
            </button>
          )}
          <button style={{...btn(),padding:"6px 14px"}} onClick={load} disabled={loading}>{loading?"⟳":"↻ Refresh"}</button>
        </div>
      </div>

      {/* Bulk cancel result */}
      {bulkMsg&&(
        <div style={{padding:"9px 14px",borderRadius:6,fontSize:12,fontWeight:600,marginBottom:12,
          color:bulkMsg.type==="success"?C.green:C.yellow,
          background:bulkMsg.type==="success"?`${C.green}18`:`${C.yellow}18`,
          border:`1px solid ${bulkMsg.type==="success"?C.green:C.yellow}44`}}>
          {bulkMsg.text}
        </div>
      )}

      {/* Select-all hint */}
      {cancellableOrders.length>0&&(
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,fontSize:12,color:C.textDim}}>
          <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
            style={{width:15,height:15,cursor:"pointer",accentColor:C.accent}}/>
          <span>Select all cancellable orders ({cancellableOrders.length})</span>
          {selected.size>0&&<span style={{color:C.accent,fontWeight:600}}>{selected.size} selected</span>}
        </div>
      )}

      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr>{cols.map(c=><th key={c} style={th}>{c}</th>)}</tr></thead>
          <tbody>
            {loading?<LoadingRow cols={cols.length}/>:
             displayed.length===0?<EmptyRow cols={cols.length} msg="No orders found"/>:
            displayed.map((o,i)=>{
              const canCancel=cancellable.includes(o.status);
              const isSel=selected.has(o.norenordno);
              return (
                <tr key={i} style={{
                  background:isSel?`${C.accent}12`:i%2===0?"transparent":`${C.surface}60`,
                  transition:"background 0.1s"
                }}>
                  {/* Checkbox */}
                  <td style={{...td,width:32,textAlign:"center"}}>
                    {canCancel&&(
                      <input type="checkbox" checked={isSel} onChange={()=>toggleOne(o.norenordno)}
                        style={{width:15,height:15,cursor:"pointer",accentColor:C.accent}}/>
                    )}
                  </td>
                  <td style={{...td,fontSize:11,whiteSpace:"nowrap"}}>
                    <div style={{fontWeight:600,color:C.text}}>{o.norentm||o.exch_tm||"—"}</div>
                    {o.exch_tm&&o.exch_tm!==o.norentm&&<div style={{fontSize:10,color:C.textMuted}}>Exch: {o.exch_tm}</div>}
                  </td>
                  <td style={td}><strong>{o.tsym}</strong></td>
                  <td style={td}>{o.exch}</td>
                  <td style={{...td,color:o.trantype==="B"?C.green:C.red,fontWeight:700}}>{o.trantype==="B"?"BUY":"SELL"}</td>
                  <td style={td}>
                    <div>{o.qty}</div>
                    {o.fillshares&&parseInt(o.fillshares)>0&&<div style={{fontSize:10,color:C.green}}>Filled: {o.fillshares}</div>}
                  </td>
                  <td style={td}>
                    <div>{fmt(o.prc)}</div>
                    {o.avgprc&&parseFloat(o.avgprc)>0&&parseFloat(o.avgprc)!==parseFloat(o.prc)&&<div style={{fontSize:10,color:C.textDim}}>Avg: {fmt(o.avgprc)}</div>}
                  </td>
                  <td style={td}>{prdAlias(o.prd)}</td>
                  <td style={td}>
                    <StatusPill status={o.status}/>
                    {o.rejreason&&o.rejreason.trim()&&o.rejreason.trim()!==" "&&(
                      <div style={{fontSize:10,color:C.red,marginTop:2,maxWidth:160,wordBreak:"break-word"}}>{o.rejreason}</div>
                    )}
                  </td>
                  <td style={{...td,fontSize:11,color:C.textDim,fontFamily:"monospace"}}>{o.norenordno}</td>
                  <td style={td}>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      {canCancel&&(
                        <button style={{...btn(),padding:"4px 10px",fontSize:11,background:`${C.accent}20`,color:C.accent,border:`1px solid ${C.accent}44`}}
                          onClick={()=>setModifyOrder_(o)}>
                          ✏️
                        </button>
                      )}
                      {canCancel&&!isSel&&(
                        <button style={{...btn("danger"),padding:"4px 10px",fontSize:11,opacity:cancelling[o.norenordno]?0.6:1}}
                          onClick={()=>cancel(o.norenordno)} disabled={!!cancelling[o.norenordno]}>
                          {cancelling[o.norenordno]?"…":"✕"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modify Order Modal */}
      {modifyOrder_&&(
        <ModifyOrderModal
          uid={uid}
          order={modifyOrder_}
          token={token}
          onClose={()=>setModifyOrder_(null)}
          onDone={load}
        />
      )}
    </div>
  );
}

// ── Trade Book ────────────────────────────────────────────────────────────────
function TradeBookTab({uid,actid,token}) {
  const [trades,setTrades]=useState(null);
  const [loading,setLoading]=useState(false);
  async function load(){setLoading(true);const r=await getTradeBook(uid,actid,token);setTrades(Array.isArray(r)?r:[]);setLoading(false);}
  useEffect(()=>{load();},[]);
  const cols=["Symbol","Exchange","Type","Qty","Fill Qty","Fill Price","Avg Price","Product","Time"];
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:16,fontWeight:700,color:C.text}}>Trade Book</div>
        <button style={{...btn(),padding:"6px 14px"}} onClick={load}>{loading?"⟳":"↻ Refresh"}</button>
      </div>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr>{cols.map(c=><th key={c} style={th}>{c}</th>)}</tr></thead>
          <tbody>
            {loading?<LoadingRow cols={cols.length}/>:!trades||trades.length===0?<EmptyRow cols={cols.length} msg="No trades today"/>:
            trades.map((t,i)=>(
              <tr key={i} style={{background:i%2===0?"transparent":`${C.surface}60`}}>
                <td style={td}><strong>{t.tsym}</strong></td>
                <td style={td}>{t.exch}</td>
                <td style={{...td,color:t.trantype==="B"?C.green:C.red,fontWeight:700}}>{t.trantype==="B"?"BUY":"SELL"}</td>
                <td style={td}>{t.qty}</td><td style={td}>{t.flqty}</td>
                <td style={td}>{fmt(t.flprc)}</td><td style={td}>{t.avgprc?fmt(t.avgprc):"—"}</td>
                <td style={td}>{prdAlias(t.prd)}</td>
                <td style={{...td,fontSize:11,color:C.textDim}}>{t.fltm}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Positions ─────────────────────────────────────────────────────────────────
function PositionsTab({uid,actid,token}) {
  const [positions,setPositions]=useState(null);
  const [loading,setLoading]=useState(false);
  const [sqMsg,setSqMsg]=useState(null);   // {type,text}
  const [sqLoading,setSqLoading]=useState({});

  async function load(){setLoading(true);const r=await getPositionBook(uid,actid,token);setPositions(Array.isArray(r)?r:[]);setLoading(false);}
  useEffect(()=>{load();},[]);

  // Square off a single position — place reverse order at MARKET price
  async function squareOff(p) {
    const netqty = parseInt(p.netqty||0);
    if(netqty===0){setSqMsg({type:"warn",text:`${p.tsym} — Net qty is 0, nothing to square off.`});return;}
    const side  = netqty > 0 ? "S" : "B";  // long → SELL, short → BUY
    const qty   = Math.abs(netqty).toString();
    setSqLoading(prev=>({...prev,[p.tsym]:true}));
    setSqMsg(null);
    const params = {
      uid, actid,
      exch:    p.exch,
      tsym:    p.tsym,
      qty,
      prc:     "0",        // market order
      prd:     p.prd,
      trantype:side,
      prctyp: "MKT",
      ret:    "DAY",
      remarks:"SquareOff",
      ordersource:"API",
    };
    const res = await placeOrder(params, token);
    setSqLoading(prev=>({...prev,[p.tsym]:false}));
    if(res.stat==="Ok"){
      setSqMsg({type:"success",text:`✅ ${p.tsym} squared off! Order: ${res.norenordno}`});
      setTimeout(load, 1500);
    } else {
      setSqMsg({type:"error",text:`❌ ${p.tsym}: ${res.emsg}`});
    }
  }

  // Square off ALL open positions at once
  async function squareOffAll() {
    const open = (positions||[]).filter(p=>parseInt(p.netqty||0)!==0);
    if(!open.length){setSqMsg({type:"warn",text:"No open positions to square off."});return;}
    if(!window.confirm(`Square off ALL ${open.length} open positions at MARKET price?`))return;
    setSqMsg({type:"info",text:`Squaring off ${open.length} positions…`});
    for(const p of open) await squareOff(p);
    setSqMsg({type:"success",text:`✅ All ${open.length} positions squared off!`});
  }

  const totalRpnl  =positions?.reduce((s,p)=>s+parseFloat(p.rpnl||0),0)||0;
  const totalUrmtom=positions?.reduce((s,p)=>s+parseFloat(p.urmtom||0),0)||0;
  const totalMtm   =totalRpnl+totalUrmtom;
  const openCount  =(positions||[]).filter(p=>parseInt(p.netqty||0)!==0).length;
  const cols=["Symbol","Exch","Product","Net Qty","Net Avg","LTP","Realized P&L","Unrealized MTM","BEP","Action"];

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:16,fontWeight:700,color:C.text}}>Positions</div>
        <div style={{display:"flex",gap:8}}>
          {openCount>0&&(
            <button style={{...btn("danger"),padding:"6px 16px",fontSize:13,fontWeight:700}}
              onClick={squareOffAll}>
              ⚡ Square Off All ({openCount})
            </button>
          )}
          <button style={{...btn(),padding:"6px 14px"}} onClick={load}>{loading?"⟳":"↻ Refresh"}</button>
        </div>
      </div>

      {/* Alert message */}
      {sqMsg&&(
        <div style={{padding:"10px 16px",borderRadius:8,marginBottom:14,fontSize:13,fontWeight:600,
          color:sqMsg.type==="success"?C.green:sqMsg.type==="error"?C.red:sqMsg.type==="warn"?C.yellow:C.textDim,
          background:sqMsg.type==="success"?`${C.green}18`:sqMsg.type==="error"?`${C.red}18`:sqMsg.type==="warn"?`${C.yellow}18`:`${C.border}`,
          border:`1px solid ${sqMsg.type==="success"?C.green:sqMsg.type==="error"?C.red:sqMsg.type==="warn"?C.yellow:C.border}44`}}>
          {sqMsg.text}
        </div>
      )}

      {/* Summary cards */}
      {positions&&positions.length>0&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
          {[["Overall MTM",totalMtm],["Realized P&L",totalRpnl],["Unrealized MTM",totalUrmtom]].map(([l,v])=>(
            <div key={l} style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 16px",textAlign:"center"}}>
              <div style={{fontSize:11,color:C.textDim,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:6}}>{l}</div>
              <div style={{fontSize:22,fontWeight:800,color:pnlColor(v)}}>{v>=0?"+":""}{fmt(v)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr>{cols.map(c=><th key={c} style={th}>{c}</th>)}</tr></thead>
          <tbody>
            {loading?<LoadingRow cols={cols.length}/>:!positions||positions.length===0?<EmptyRow cols={cols.length} msg="No open positions"/>:
            positions.map((p,i)=>{
              const rpnl=parseFloat(p.rpnl||0),urmtom=parseFloat(p.urmtom||0),netqty=parseInt(p.netqty||0);
              const isLoading=!!sqLoading[p.tsym];
              return (
                <tr key={i} style={{background:i%2===0?"transparent":`${C.surface}60`}}>
                  <td style={td}><strong>{p.tsym}</strong></td>
                  <td style={td}>{p.exch}</td>
                  <td style={td}>{prdAlias(p.prd)}</td>
                  <td style={{...td,color:netqty>0?C.green:netqty<0?C.red:C.textMuted,fontWeight:700}}>
                    {netqty>0?"▲":netqty<0?"▼":""} {p.netqty}
                  </td>
                  <td style={td}>{fmt(p.netavgprc)}</td>
                  <td style={td}>{p.lp?fmt(p.lp):"—"}</td>
                  <td style={{...td,color:pnlColor(rpnl),fontWeight:600}}>{rpnl>=0?"+":""}{fmt(rpnl)}</td>
                  <td style={{...td,color:pnlColor(urmtom),fontWeight:600}}>{urmtom>=0?"+":""}{fmt(urmtom)}</td>
                  <td style={td}>{p.bep?fmt(p.bep):"—"}</td>
                  <td style={td}>
                    {netqty!==0?(
                      <button
                        style={{...btn("danger"),padding:"4px 12px",fontSize:11,fontWeight:700,
                          opacity:isLoading?0.6:1,
                          background:netqty>0?`${C.red}22`:`${C.green}22`,
                          color:netqty>0?C.red:C.green,
                          border:`1px solid ${netqty>0?C.red:C.green}44`}}
                        onClick={()=>squareOff(p)}
                        disabled={isLoading}
                        title={netqty>0?"Square Off (Sell)":"Square Off (Buy)"}
                      >
                        {isLoading?"…":netqty>0?"▼ Sq.Off":"▲ Sq.Off"}
                      </button>
                    ):<span style={{color:C.textMuted,fontSize:11}}>Closed</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {positions&&positions.length>0&&(
            <tfoot>
              <tr style={{borderTop:`2px solid ${C.border}`,background:C.surface}}>
                <td colSpan={6} style={{...td,fontSize:11,color:C.textDim,fontWeight:700,textTransform:"uppercase"}}>Total</td>
                <td style={{...td,fontWeight:800,color:pnlColor(totalRpnl)}}>{totalRpnl>=0?"+":""}{fmt(totalRpnl)}</td>
                <td style={{...td,fontWeight:800,color:pnlColor(totalUrmtom)}}>{totalUrmtom>=0?"+":""}{fmt(totalUrmtom)}</td>
                <td colSpan={2} style={td}/>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}


// ── Holdings ──────────────────────────────────────────────────────────────────
function HoldingsTab({uid,actid,token}) {
  const [holdings,setHoldings]=useState(null);
  const [ltpMap,setLtpMap]=useState({});
  const [loading,setLoading]=useState(false);
  const [ltpLoading,setLtpLoading]=useState(false);
  const [showZero,setShowZero]=useState(false);
  const [exchFilter,setExchFilter]=useState("ALL");
  const [searchQ,setSearchQ]=useState("");
  const [exitMsg,setExitMsg]=useState(null);
  const [exitLoading,setExitLoading]=useState({});

  async function exitHolding(sym, exch, qty, ltp) {
    const sellQty = parseInt(qty||0);
    if(sellQty<=0){setExitMsg({type:"warn",text:`${sym} — No quantity to exit.`});return;}
    if(!window.confirm(`Exit ${sym} — SELL ${sellQty} qty at MARKET price?`))return;
    setExitLoading(p=>({...p,[sym]:true}));
    setExitMsg(null);
    const params = {
      uid, actid,
      exch,
      tsym:    sym,
      qty:     sellQty.toString(),
      prc:     "0",
      prd:     "C",        // CNC for delivery holdings
      trantype:"S",
      prctyp:  "MKT",
      ret:     "DAY",
      remarks: "ExitHolding",
      ordersource:"API",
    };
    const res = await placeOrder(params, token);
    setExitLoading(p=>({...p,[sym]:false}));
    if(res.stat==="Ok"){
      setExitMsg({type:"success",text:`✅ ${sym} exit order placed! Order: ${res.norenordno}`});
      setTimeout(load, 1500);
    } else {
      setExitMsg({type:"error",text:`❌ ${sym}: ${res.emsg}`});
    }
  }

  async function load(){
    setLoading(true);
    const r=await getHoldings(uid,actid,"C",token);
    const data=Array.isArray(r)?r:[];
    setHoldings(data);setLoading(false);fetchLtps(data);
  }
  async function fetchLtps(data){
    if(!data||!data.length)return;
    setLtpLoading(true);
    const map={};
    for(const h of data){
      const sym=h.exch_tsym?.[0];
      if(!sym?.token||!sym?.exch)continue;
      try{const q=await apiPost("/GetQuotes",{uid,exch:sym.exch,token:sym.token},token);if(q?.lp)map[sym.tsym]=parseFloat(q.lp);}catch(e){}
    }
    setLtpMap(map);setLtpLoading(false);
  }
  useEffect(()=>{load();},[]);

  const rows=(holdings||[]).map(h=>{
    const sym=h.exch_tsym?.[0]||{};
    const qty=parseInt(h.holdqty||0),colqty=parseInt(h.colqty||0),brkcolqty=parseInt(h.brkcolqty||0),dpqty=parseInt(h.dpqty||0),usedqty=parseInt(h.usedqty||0),btstqty=parseInt(h.btstqty||0);
    const avgCost=parseFloat(h.upldprc||0);
    const ltp=ltpMap[sym.tsym]||parseFloat(sym.lp||0);
    const totalQty=qty+colqty+brkcolqty;
    const invVal=avgCost*totalQty,curVal=ltp>0?ltp*totalQty:0;
    const pnl=ltp>0&&totalQty>0?(ltp-avgCost)*totalQty:0;
    const pnlPct=avgCost>0&&ltp>0?((ltp-avgCost)/avgCost*100):0;
    return{sym,qty,colqty,brkcolqty,dpqty,usedqty,btstqty,avgCost,ltp,totalQty,invVal,curVal,pnl,pnlPct};
  });

  const totalInv=rows.reduce((s,r)=>s+r.invVal,0);
  const totalCur=rows.reduce((s,r)=>s+r.curVal,0);
  const totalPnl=totalCur-totalInv;
  const totalPct=totalInv>0?(totalPnl/totalInv*100):0;
  const totalColVal=rows.reduce((s,r)=>s+(r.colqty+r.brkcolqty)*r.avgCost,0);

  const filtered=rows.filter(r=>{
    if(!showZero&&r.totalQty===0)return false;
    if(exchFilter!=="ALL"&&r.sym.exch!==exchFilter)return false;
    if(searchQ&&!r.sym.tsym?.toLowerCase().includes(searchQ.toLowerCase())&&!r.sym.cname?.toLowerCase().includes(searchQ.toLowerCase()))return false;
    return true;
  });

  const exBtnStyle=ex=>({padding:"5px 14px",border:"none",cursor:"pointer",fontSize:12,fontWeight:600,borderRadius:6,transition:"all 0.15s",background:exchFilter===ex?C.accent:C.surface,color:exchFilter===ex?"#000":C.textDim});

  return (
    <div>
      {/* Summary bar */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px 20px",marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{fontSize:17,fontWeight:800,color:C.text}}>Holdings</div>
            <div style={{background:`${C.accent}22`,border:`1px solid ${C.accent}44`,borderRadius:6,padding:"3px 10px",fontSize:12,fontWeight:700,color:C.accent}}>{rows.filter(r=>r.totalQty>0).length} stocks</div>
            {ltpLoading&&<div style={{fontSize:11,color:C.yellow}}>⟳ Fetching live prices…</div>}
          </div>
          <div style={{display:"flex",gap:8}}>
            <div style={{display:"flex",gap:4,background:C.surface,borderRadius:8,padding:3}}>
              {["ALL","NSE","BSE","NFO","BFO","MCX","CDS"].map(ex=><button key={ex} style={exBtnStyle(ex)} onClick={()=>setExchFilter(ex)}>{ex}</button>)}
            </div>
            <button style={{...btn(),padding:"5px 12px",fontSize:12}} onClick={()=>setShowZero(v=>!v)}>{showZero?"Hide 0 Qty":"Show 0 Qty"}</button>
            <button style={{...btn("primary"),padding:"5px 14px",fontSize:12}} onClick={load}>{loading?"⟳":"↻ Refresh"}</button>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:0,borderTop:`1px solid ${C.border}`,paddingTop:14}}>
          {[["Total Investment",fmt(totalInv),C.text,null],["Current Value",fmt(totalCur),C.text,null],["P&L",`${totalPnl>=0?"+":""}${fmt(totalPnl)}`,pnlColor(totalPnl),`${totalPnl>=0?"+":""}${totalPct.toFixed(2)}%`],["Collateral Value",fmt(totalColVal),C.yellow,`${rows.reduce((s,r)=>s+r.colqty+r.brkcolqty,0)} qty pledged`]].map(([label,val,color,sub],i)=>(
            <div key={label} style={{paddingRight:i<3?20:0,borderRight:i<3?`1px solid ${C.border}`:"none",marginRight:i<3?20:0}}>
              <div style={{fontSize:11,color:C.textDim,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4}}>{label}</div>
              <div style={{fontSize:20,fontWeight:800,color}}>{val}</div>
              {sub&&<div style={{fontSize:12,color,marginTop:2,fontWeight:600}}>{sub}</div>}
            </div>
          ))}
        </div>
      </div>
      {/* Exit alert */}
      {exitMsg&&(
        <div style={{padding:"10px 16px",borderRadius:8,marginBottom:12,fontSize:13,fontWeight:600,
          color:exitMsg.type==="success"?C.green:exitMsg.type==="error"?C.red:exitMsg.type==="warn"?C.yellow:C.textDim,
          background:exitMsg.type==="success"?`${C.green}18`:exitMsg.type==="error"?`${C.red}18`:exitMsg.type==="warn"?`${C.yellow}18`:`${C.border}`,
          border:`1px solid ${exitMsg.type==="success"?C.green:exitMsg.type==="error"?C.red:exitMsg.type==="warn"?C.yellow:C.border}44`}}>
          {exitMsg.text}
        </div>
      )}

      {/* Search */}
      <div style={{marginBottom:12}}>
        <input style={inp} placeholder="Search symbol or company…" value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
      </div>
      {/* Table */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden"}}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:C.surface}}>
                {["Instrument","Qty","Saleable","Col Qty","Avg Cost","Inv. Value","LTP","Cur. Value","P&L","Action"].map(c=><th key={c} style={{...th,background:"transparent"}}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading?<LoadingRow cols={9}/>:filtered.length===0?<EmptyRow cols={9} msg="No holdings found"/>:
              filtered.map((r,i)=>(
                <tr key={i} style={{borderBottom:`1px solid ${C.border}20`}} onMouseEnter={e=>e.currentTarget.style.background=`${C.surface}80`} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <td style={{...td,minWidth:180}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:4,height:36,borderRadius:2,background:r.sym.exch==="NSE"?C.accent:C.yellow,flexShrink:0}}/>
                      <div>
                        <div style={{fontWeight:700,fontSize:13}}>{r.sym.tsym||"—"}</div>
                        <div style={{fontSize:10,color:C.textDim}}>{r.sym.cname}</div>
                        <div style={{fontSize:10,color:r.sym.exch==="NSE"?C.accent:C.yellow,fontWeight:600}}>{r.sym.exch}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{...td,textAlign:"right",fontWeight:700,color:r.qty>0?C.text:C.textMuted}}>{r.qty}</td>
                  <td style={{...td,textAlign:"right",color:C.textDim}}>{Math.max(0,r.qty-r.usedqty)}</td>
                  <td style={{...td,textAlign:"right"}}>
                    {(r.colqty+r.brkcolqty)>0?<span style={{color:C.yellow,fontWeight:700}}>{r.colqty+r.brkcolqty} 🔒</span>:<span style={{color:C.textMuted}}>0</span>}
                  </td>
                  <td style={{...td,textAlign:"right",color:C.textDim}}>{r.avgCost>0?fmt(r.avgCost):"—"}</td>
                  <td style={{...td,textAlign:"right"}}>{r.invVal>0?fmt(r.invVal):"—"}</td>
                  <td style={{...td,textAlign:"right",fontWeight:700,color:ltpMap[r.sym.tsym]?C.accent:C.text}}>{r.ltp>0?fmt(r.ltp):"—"}</td>
                  <td style={{...td,textAlign:"right",fontWeight:600}}>{r.curVal>0?fmt(r.curVal):"—"}</td>
                  <td style={{...td,textAlign:"right"}}>
                    {r.totalQty>0&&r.ltp>0?(
                      <div>
                        <div style={{fontWeight:700,color:pnlColor(r.pnl)}}>{r.pnl>=0?"+":""}{fmt(r.pnl)}</div>
                        <div style={{fontSize:11,color:pnlColor(r.pnlPct)}}>{r.pnlPct>=0?"+":""}{r.pnlPct.toFixed(2)}%</div>
                      </div>
                    ):<span style={{color:C.textMuted}}>—</span>}
                  </td>
                  <td style={{...td,textAlign:"center"}}>
                    {r.qty>0&&(
                      <button
                        style={{background:`${C.red}22`,color:C.red,border:`1px solid ${C.red}44`,
                          padding:"5px 12px",borderRadius:6,fontSize:12,fontWeight:700,cursor:"pointer",
                          opacity:exitLoading[r.sym.tsym]?0.6:1}}
                        onClick={()=>exitHolding(r.sym.tsym,r.sym.exch,r.qty,r.ltp)}
                        disabled={!!exitLoading[r.sym.tsym]}
                        title={`Sell ${r.qty} qty at market price`}
                      >
                        {exitLoading[r.sym.tsym]?"…":"▼ Exit"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {filtered.length>0&&(
              <tfoot>
                <tr style={{background:C.surface,borderTop:`2px solid ${C.border}`}}>
                  <td style={{...td,fontWeight:700,color:C.textDim,fontSize:11,textTransform:"uppercase"}}>Total ({filtered.length})</td>
                  <td style={{...td,textAlign:"right",fontWeight:700}}>{filtered.reduce((s,r)=>s+r.qty,0)}</td>
                  <td style={td}/>
                  <td style={{...td,textAlign:"right",fontWeight:700,color:C.yellow}}>{filtered.reduce((s,r)=>s+r.colqty+r.brkcolqty,0)}</td>
                  <td style={td}/><td style={{...td,textAlign:"right",fontWeight:700}}>{fmt(filtered.reduce((s,r)=>s+r.invVal,0))}</td>
                  <td style={td}/><td style={{...td,textAlign:"right",fontWeight:700}}>{fmt(filtered.reduce((s,r)=>s+r.curVal,0))}</td>
                  <td style={{...td,textAlign:"right",fontWeight:800,color:pnlColor(totalPnl)}}>
                    {totalPnl>=0?"+":""}{fmt(totalPnl)}
                    <div style={{fontSize:11,color:pnlColor(totalPct)}}>{totalPct>=0?"+":""}{totalPct.toFixed(2)}%</div>
                  </td>
                  <td style={td}/>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Funds ─────────────────────────────────────────────────────────────────────
function FundsTab({uid,actid,token}) {
  const [limits,setLimits]=useState(null);
  const [loading,setLoading]=useState(false);
  async function load(){setLoading(true);const r=await getLimits(uid,actid,token);setLimits(r);setLoading(false);}
  useEffect(()=>{load();},[]);
  if(loading)return <div style={{color:C.textDim,padding:40,textAlign:"center"}}>⟳ Loading funds…</div>;
  if(!limits||limits.stat==="Not_Ok")return <div style={{color:C.red,padding:24}}>{limits?.emsg||"Failed to load."}</div>;
  const cash=parseFloat(limits.cash||0),payin=parseFloat(limits.payin||0),brkColl=parseFloat(limits.brkcollamt||0);
  const collateral=parseFloat(limits.collateral||0),cashColl=parseFloat(limits.cash_coll||0),grcoll=parseFloat(limits.grcoll||0);
  const mu=parseFloat(limits.marginused||0),unmtom=parseFloat(limits.unmtom||0),rpnl=parseFloat(limits.rpnl||0);
  const span=parseFloat(limits.span||0),expo=parseFloat(limits.expo||0),premium=parseFloat(limits.premium||0),daycash=parseFloat(limits.daycash||0);
  const totalColl=brkColl+collateral+cashColl;
  const totalCredits=cash+payin+totalColl+daycash;
  const avail=totalCredits-mu;
  const summaryCards=[["Available Margin",fmt(avail),avail>=0?C.green:C.red],["Total Credits",fmt(totalCredits),C.text],["Cash Margin",fmt(cash),C.accent],["Collateral",fmt(totalColl),totalColl>0?C.yellow:C.textDim],["Margin Used",fmt(mu),mu>0?C.red:C.textDim],["Unrealized MTM",fmt(unmtom),unmtom>=0?C.green:C.red]];
  const rows=[["cash — Cash Margin",cash],["payin — Pay-in",payin],["payout — Pay-out",limits.payout||0],["daycash — Additional Leverage",daycash],["brkcollamt — Broker Collateral",brkColl],["collateral — Holding Collateral",collateral],["cash_coll — Cash Collateral",cashColl],["grcoll — Gross Collateral (pre-cut)",grcoll],["unclearedcash — Uncleared Cash",limits.unclearedcash||0],["marginused — Margin Used",mu],["span — SPAN Margin",span],["expo — Exposure Margin",expo],["premium — Premium Used",premium],["unmtom — Unrealized MTM",unmtom],["rpnl — Realized P&L",rpnl],["grexpo — Gross Exposure",limits.grexpo||0],["turnover — Turnover",limits.turnover||0]];
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:16,fontWeight:700,color:C.text}}>Funds & Limits</div>
        <button style={{...btn(),padding:"6px 14px"}} onClick={load}>↻ Refresh</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20}}>
        {summaryCards.map(([label,val,color])=>(
          <div key={label} style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 16px",textAlign:"center"}}>
            <div style={{fontSize:11,color:C.textDim,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4}}>{label}</div>
            <div style={{fontSize:20,fontWeight:800,color}}>{val}</div>
          </div>
        ))}
      </div>
      <div style={{fontSize:11,color:C.textDim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8}}>Detailed Breakdown (API Field Names)</div>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <tbody>{rows.map(([label,val],i)=>(
          <tr key={i} style={{background:i%2===0?"transparent":`${C.surface}60`}}>
            <td style={{...td,color:C.textDim,fontFamily:"monospace",fontSize:12}}>{label}</td>
            <td style={{...td,textAlign:"right",fontWeight:600,color:parseFloat(val)<0?C.red:C.text}}>{fmt(val)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

// ── Account Form (reused for Master & Child) ──────────────────────────────────
function AccountForm({title,color,icon,accounts,onSave,onEdit,onDelete,editId,cancelEdit,isMaster}) {
  const empty = {label:"",uid:"",actid:"",secret_key:"",access_token:""};
  const [form,setForm]       = useState(empty);
  const [err,setErr]         = useState("");
  const [authCode,setAuthCode] = useState("");
  const [genLoading,setGenLoading] = useState(false);
  const [genErr,setGenErr]   = useState("");
  const [genOk,setGenOk]     = useState("");
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  // Load account into form when editing
  useEffect(()=>{
    if(editId){
      const a=accounts.find(x=>x.id===editId);
      if(a) setForm({label:a.label||"",uid:a.uid||"",actid:a.actid||"",secret_key:a.secret_key||"",access_token:a.access_token||""});
    } else {
      setForm(empty);
    }
    setErr(""); setAuthCode(""); setGenErr(""); setGenOk("");
  },[editId]);

  // Generate access token from secret key + auth code
  async function generateToken(){
    const uid = form.uid.trim();
    const sk  = form.secret_key.trim();
    const code= authCode.trim();
    if(!uid)  {setGenErr("Enter User ID first.");return;}
    if(!sk)   {setGenErr("Enter Secret Key first.");return;}
    if(!code) {setGenErr("Paste the auth code from the redirect URL.");return;}
    setGenLoading(true); setGenErr(""); setGenOk("");
    try{
      const res  = await fetch("/api/token",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({code,client_id:uid,secret_key:sk})});
      const data = await res.json();
      if(data.stat==="Ok"&&data.access_token){
        setForm(f=>({...f,access_token:data.access_token}));
        setGenOk("✅ Token generated! Click Save Changes.");
        setAuthCode("");
      } else {
        setGenErr("❌ "+(data.emsg||"Failed")+" — Get a fresh auth code and try again.");
      }
    }catch(e){setGenErr("❌ "+e.message);}
    setGenLoading(false);
  }

  function save(){
    if(!form.uid.trim())  {setErr("User ID (uid) is required.");return;}
    if(!form.actid.trim()){setErr("Account ID (actid) is required.");return;}
    if(isMaster){
      // Master uses session token — no access_token needed
      setErr("");
      onSave({...form,id:editId||Date.now().toString(),access_token:"__SESSION__"});
      setForm(empty); return;
    }
    if(!form.access_token.trim()){setErr("Generate a token first using the button below.");return;}
    setErr("");
    onSave({...form,id:editId||Date.now().toString()});
    setForm(empty); setAuthCode(""); setGenErr(""); setGenOk("");
  }

  const loginUrl = `https://trade.iseindia.com/OAuthlogin/authorize/oauth?client_id=${form.uid||"(enter uid first)"}`;

  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
      {/* ── Form ── */}
      <div style={{background:C.panel,border:`1px solid ${color}44`,borderRadius:12,padding:20}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
          <span style={{fontSize:18}}>{icon}</span>
          <div style={{fontSize:14,fontWeight:700,color:C.text}}>{editId?`✏️ Edit ${title}`:`➕ Add ${title}`}</div>
        </div>

        {/* Label */}
        <div style={{marginBottom:12}}>
          <label style={lbl}>Label <span style={{color:C.textMuted,fontWeight:400}}>(optional)</span></label>
          <input style={inp} placeholder="e.g. Vijay — Main Account" value={form.label} onChange={set("label")}/>
        </div>

        {/* uid */}
        <div style={{marginBottom:12}}>
          <label style={lbl}>User ID (uid) *</label>
          <input style={inp} placeholder="e.g. 92CHV00017_U" value={form.uid} onChange={e=>setForm(f=>({...f,uid:e.target.value,actid:e.target.value}))}/>
          <div style={{fontSize:11,color:C.textMuted,marginTop:3}}>Kambala User ID — actid auto-filled to same value</div>
        </div>

        {/* actid */}
        <div style={{marginBottom:16}}>
          <label style={lbl}>Account ID (actid) *</label>
          <input style={inp} placeholder="e.g. 92CHV00017_U" value={form.actid} onChange={set("actid")}/>
          <div style={{fontSize:11,color:C.textMuted,marginTop:3}}>Usually same as uid</div>
        </div>

        {/* Master: show session token info. Child: show secret key + token generator */}
        {isMaster ? (
          <div style={{background:`${C.green}12`,border:`1px solid ${C.green}33`,borderRadius:8,padding:"12px 14px",marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:C.green,marginBottom:4}}>✅ Uses Your Login Session Token</div>
            <div style={{fontSize:11,color:C.textDim,lineHeight:1.7}}>
              The master account automatically uses the token from your current login session. No separate token management needed — it refreshes every time you log in.
            </div>
          </div>
        ) : (
          <div style={{marginBottom:16}}>
            {/* Secret Key */}
            <label style={lbl}>Secret Key *</label>
            <input style={{...inp,fontFamily:"monospace",fontSize:12}} type="password"
              placeholder="From trade.iseindia.com → API Key Generation page"
              value={form.secret_key} onChange={set("secret_key")}/>
            <div style={{fontSize:11,color:C.textMuted,marginTop:3,marginBottom:12}}>
              Found at trade.iseindia.com → API Key Generation page for this account
            </div>

            {/* Token Generator */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:14}}>
              <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:10}}>⚡ Generate Daily Token</div>

              {/* Step 1 */}
              <div style={{fontSize:11,color:C.textDim,marginBottom:6,lineHeight:1.7}}>
                <span style={{color:C.accent,fontWeight:700}}>Step 1</span> — Login as this account:
              </div>
              <a href={loginUrl} target="_blank" rel="noreferrer"
                style={{...btn("primary"),display:"inline-flex",alignItems:"center",gap:6,textDecoration:"none",fontSize:12,marginBottom:10}}>
                Login as {form.uid||"(enter uid)"} ↗
              </a>

              {/* Step 2 */}
              <div style={{fontSize:11,color:C.textDim,marginBottom:6,lineHeight:1.7}}>
                <span style={{color:C.accent,fontWeight:700}}>Step 2</span> — Copy the <code style={{color:C.accent}}>code</code> from redirect URL and paste below:
              </div>
              <input style={{...inp,fontSize:12,marginBottom:8}}
                placeholder="Paste auth code from redirect URL…"
                value={authCode} onChange={e=>setAuthCode(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&generateToken()}/>

              {genErr&&<div style={{fontSize:11,color:C.red,marginBottom:6}}>{genErr}</div>}
              {genOk &&<div style={{fontSize:11,color:C.green,marginBottom:6,fontWeight:600}}>{genOk}</div>}

              <button style={{...btn("primary"),width:"100%",padding:"9px",fontSize:12,background:color,color:"#000"}}
                onClick={generateToken} disabled={genLoading}>
                {genLoading?"⏳ Generating…":"⚡ Generate & Save Token"}
              </button>

              {/* Show current token status */}
              {form.access_token&&(
                <div style={{marginTop:8,fontSize:11,color:C.green,textAlign:"center"}}>
                  ✅ Token ready: •••{form.access_token.slice(-8)}
                </div>
              )}
            </div>
          </div>
        )}

        {err&&<div style={{background:`${C.red}15`,border:`1px solid ${C.red}44`,borderRadius:6,padding:"8px 12px",fontSize:12,color:C.red,marginBottom:12}}>{err}</div>}

        <div style={{display:"flex",gap:8}}>
          <button style={{...btn("primary"),flex:1,padding:"10px",background:color,color:"#000"}} onClick={save}>
            {editId?"💾 Save Changes":`➕ Add ${title}`}
          </button>
          {editId&&<button style={{...btn(),padding:"10px 16px"}} onClick={cancelEdit}>Cancel</button>}
        </div>
        <div style={{marginTop:10,padding:"9px 12px",background:C.surface,borderRadius:8,fontSize:11,color:C.textMuted}}>
          💾 Saved automatically in browser — persists after restart.
        </div>
      </div>

      {/* ── Accounts List ── */}
      <div>
        <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:12}}>{title}s ({accounts.length})</div>
        {accounts.length===0?(
          <div style={{background:C.panel,border:`2px dashed ${C.border}`,borderRadius:12,padding:32,textAlign:"center",color:C.textMuted,fontSize:13}}>
            No {title.toLowerCase()}s yet.<br/>Add one using the form.
          </div>
        ):accounts.map(a=>(
          <div key={a.id} style={{background:C.panel,border:`1px solid ${a.id===editId?color:C.border}`,borderRadius:10,padding:14,marginBottom:10,transition:"border 0.2s"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <span>{icon}</span>
                  <span style={{fontSize:14,fontWeight:700,color:C.text}}>{a.label||a.uid}</span>
                  {a.label&&<span style={{fontSize:11,color:C.textDim}}>({a.uid})</span>}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:"3px 12px",fontSize:12}}>
                  <span style={{color:C.textMuted,fontFamily:"monospace"}}>uid</span>
                  <span style={{color:C.text}}>{a.uid}</span>
                  <span style={{color:C.textMuted,fontFamily:"monospace"}}>actid</span>
                  <span style={{color:C.text}}>{a.actid}</span>
                  <span style={{color:C.textMuted,fontFamily:"monospace"}}>token</span>
                  <span style={{color:C.green,fontSize:11}}>
                    {a.access_token==="__SESSION__" ? "✅ Session token (auto)" :
                     a.access_token ? "✅ Token saved" : "⚠ No token — generate one"}
                  </span>
                </div>
              </div>
              <div style={{display:"flex",gap:6,marginLeft:10,flexShrink:0}}>
                <button style={{...btn(),padding:"4px 10px",fontSize:11}} onClick={()=>onEdit(a.id)}>✏️</button>
                <button style={{...btn("danger"),padding:"4px 10px",fontSize:11}} onClick={()=>onDelete(a.id)}>🗑</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Copy Trading Tab ──────────────────────────────────────────────────────────
function CopyTradingTab({
  masters, setMasters, children, setChildren, rules, setRules,
  running, log, addLog, onStart, onStop, onSync,
  loggedUid, loggedActid
}) {
  const [tab,setTab]=useState("masters");
  const [editMId,setEditMId]=useState(null);
  const [editCId,setEditCId]=useState(null);
  const emptyRule={masterId:"",childIds:[],multiplier:"1",maxOrderValue:"50000",exchanges:"NSE,BSE,NFO",products:"I,C,M",active:true};
  const [rForm,setRForm]=useState(emptyRule);
  const [rErr,setRErr]=useState("");
  const setR=k=>e=>setRForm(f=>({...f,[k]:e.target.value}));

  function saveMaster(data){setMasters(ms=>editMId?ms.map(m=>m.id===data.id?data:m):[...ms,data]);addLog(`Master ${editMId?"updated":"added"}: ${data.label||data.uid}`,"success");setEditMId(null);}
  function deleteMaster(id){setMasters(ms=>ms.filter(m=>m.id!==id));setRules(rs=>rs.filter(r=>r.masterId!==id));addLog("Master removed.","warn");}
  function saveChild(data){setChildren(cs=>editCId?cs.map(c=>c.id===data.id?data:c):[...cs,data]);addLog(`Child ${editCId?"updated":"added"}: ${data.label||data.uid}`,"success");setEditCId(null);}
  function deleteChild(id){setChildren(cs=>cs.filter(c=>c.id!==id));setRules(rs=>rs.filter(r=>!r.childIds?.includes(id)));addLog("Child removed.","warn");}

  function toggleChild(id){setRForm(f=>({...f,childIds:f.childIds.includes(id)?f.childIds.filter(c=>c!==id):[...f.childIds,id]}));}
  function addRule(){
    if(!rForm.masterId){setRErr("Select a master account.");return;}
    if(!rForm.childIds.length){setRErr("Select at least one child account.");return;}
    setRErr("");
    setRules(rs=>[...rs,{...rForm,id:Date.now().toString(),
      exchanges:rForm.exchanges.split(",").map(x=>x.trim().toUpperCase()).filter(Boolean),
      products:rForm.products.split(",").map(x=>x.trim()).filter(Boolean)}]);
    addLog(`Rule added ×${rForm.multiplier}`,"success");setRForm(emptyRule);
  }

  const tabBtn=(id,label,count)=>(
    <button onClick={()=>setTab(id)} style={{padding:"9px 18px",border:"none",cursor:"pointer",fontSize:13,fontWeight:600,borderBottom:tab===id?`2px solid ${C.accent}`:"2px solid transparent",color:tab===id?C.accent:C.textDim,background:"transparent",whiteSpace:"nowrap"}}>
      {label}{count!==undefined?` (${count})`:""}
    </button>
  );
  const logColor={info:C.textDim,success:C.green,error:C.red,warn:C.yellow};

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:18,fontWeight:800,color:C.text}}>Copy Trading Engine</div>
          <div style={{fontSize:12,color:C.textDim,marginTop:2}}>Orders from <span style={{color:C.yellow}}>Master</span> accounts are copied to <span style={{color:C.accent}}>Child</span> accounts in real time.</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{fontSize:12,color:running?C.green:C.textMuted,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
            <span style={{width:8,height:8,borderRadius:"50%",background:running?C.green:C.textMuted,display:"inline-block"}}/>
            {running?"Running":"Stopped"}
          </div>
          {!running
            ?<button style={{...btn("green"),padding:"9px 22px"}} onClick={onStart}>▶ Start Engine</button>
            :<div style={{display:"flex",gap:8}}>
              <button style={{...btn(),padding:"9px 16px",fontSize:13}} onClick={onSync}>🔄 Sync Now</button>
              <button style={{...btn("danger"),padding:"9px 22px"}} onClick={onStop}>■ Stop Engine</button>
            </div>
          }
        </div>
      </div>
      {/* Summary pills */}
      <div style={{display:"flex",gap:10,marginBottom:20}}>
        {[[`👑 ${masters.length} Master${masters.length!==1?"s":""}`,C.yellow],[`👶 ${children.length} Child${children.length!==1?"ren":""}`,C.accent],[`📋 ${rules.length} Rule${rules.length!==1?"s":""}`,C.green]].map(([label,color])=>(
          <div key={label} style={{background:`${color}15`,border:`1px solid ${color}33`,borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:600,color}}>{label}</div>
        ))}
      </div>
      {/* Tabs */}
      <div style={{borderBottom:`1px solid ${C.border}`,marginBottom:20,display:"flex",gap:0,overflowX:"auto"}}>
        {tabBtn("masters","👑 Master Accounts",masters.length)}
        {tabBtn("children","👶 Child Accounts",children.length)}
        {tabBtn("rules","📋 Copy Rules",rules.length)}
        {tabBtn("log","📜 Activity Log")}
      </div>

      {tab==="masters"&&(
        <div>
          {/* Quick Add: logged-in user as master */}
          {!masters.find(m=>m.uid===loggedUid) && (
            <div style={{background:`${C.yellow}12`,border:`1px solid ${C.yellow}33`,borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:C.yellow}}>👑 Add yourself as Master?</div>
                <div style={{fontSize:12,color:C.textDim,marginTop:2}}>Your logged-in account ({loggedUid}) can be added as a master. Token is used from your login session automatically.</div>
              </div>
              <button style={{...btn("primary"),background:C.yellow,color:"#000",whiteSpace:"nowrap",flexShrink:0}} onClick={()=>{
                saveMaster({id:Date.now().toString(),label:"My Account",uid:loggedUid,actid:loggedActid,secret_key:"",access_token:"__SESSION__"});
              }}>+ Add Me as Master</button>
            </div>
          )}
          <AccountForm title="Master Account" color={C.yellow} icon="👑" accounts={masters} onSave={saveMaster} onEdit={setEditMId} onDelete={deleteMaster} editId={editMId} cancelEdit={()=>setEditMId(null)} isMaster={true}/>
        </div>
      )}
      {tab==="children"&&<AccountForm title="Child Account" color={C.accent} icon="👶" accounts={children} onSave={saveChild} onEdit={setEditCId} onDelete={deleteChild} editId={editCId} cancelEdit={()=>setEditCId(null)} isMaster={false}/>}

      {tab==="rules"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
          <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
            <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:16}}>➕ Add Copy Rule</div>
            <div style={{marginBottom:12}}>
              <label style={lbl}>👑 Master Account (Source) *</label>
              <select style={{...inp,appearance:"none"}} value={rForm.masterId} onChange={setR("masterId")}>
                <option value="">Select master…</option>
                {masters.map(m=><option key={m.id} value={m.id}>{m.label||m.uid}</option>)}
              </select>
            </div>
            <div style={{marginBottom:12}}>
              <label style={lbl}>👶 Child Accounts (Targets) *</label>
              {children.length===0
                ?<div style={{background:`${C.yellow}15`,border:`1px solid ${C.yellow}33`,borderRadius:8,padding:"10px 12px",fontSize:12,color:C.yellow}}>⚠ Add child accounts first.</div>
                :<div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:10,display:"flex",flexDirection:"column",gap:6}}>
                  {children.map(c=>(
                    <label key={c.id} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"5px 8px",borderRadius:6,background:rForm.childIds.includes(c.id)?`${C.accent}18`:"transparent",border:`1px solid ${rForm.childIds.includes(c.id)?C.accent+"44":"transparent"}`}}>
                      <input type="checkbox" checked={rForm.childIds.includes(c.id)} onChange={()=>toggleChild(c.id)} style={{width:16,height:16,cursor:"pointer",accentColor:C.accent}}/>
                      <div><div style={{fontSize:13,fontWeight:600,color:C.text}}>{c.label||c.uid}</div><div style={{fontSize:11,color:C.textDim}}>{c.uid}</div></div>
                    </label>
                  ))}
                </div>
              }
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              <div><label style={lbl}>Qty Multiplier</label><input style={inp} type="number" min="0.1" step="0.1" value={rForm.multiplier} onChange={setR("multiplier")}/><div style={{fontSize:10,color:C.textMuted,marginTop:3}}>1=same | 0.5=half | 2=double</div></div>
              <div><label style={lbl}>Max Order Value (₹)</label><input style={inp} type="number" value={rForm.maxOrderValue} onChange={setR("maxOrderValue")}/><div style={{fontSize:10,color:C.textMuted,marginTop:3}}>0 = no limit</div></div>
            </div>
            <div style={{marginBottom:12}}><label style={lbl}>exch — Exchanges</label><input style={inp} value={rForm.exchanges} onChange={setR("exchanges")} placeholder="NSE,BSE,NFO,BFO"/><div style={{fontSize:11,color:C.textMuted,marginTop:3}}>NSE | BSE | NFO | BFO | MCX | CDS</div></div>
            <div style={{marginBottom:16}}>
              <label style={lbl}>prd — Products to Copy</label>
              <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:10,display:"flex",flexDirection:"column",gap:5}}>
                {PRODUCTS.map(({prd,alias,label:plabel,exch})=>{
                  const sel=rForm.products.split(",").map(x=>x.trim()).includes(prd);
                  return (
                    <label key={prd} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"5px 8px",borderRadius:6,background:sel?`${C.accent}18`:"transparent",border:`1px solid ${sel?C.accent+"44":"transparent"}`}}>
                      <input type="checkbox" checked={sel} onChange={()=>{
                        const cur=rForm.products.split(",").map(x=>x.trim()).filter(Boolean);
                        setRForm(f=>({...f,products:(sel?cur.filter(x=>x!==prd):[...cur,prd]).join(",")}));
                      }} style={{width:16,height:16,cursor:"pointer",accentColor:C.accent}}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:600,color:sel?C.text:C.textDim}}><span style={{fontFamily:"monospace",color:sel?C.accent:C.textMuted,marginRight:6}}>{prd}</span>{plabel}</div>
                        <div style={{fontSize:10,color:C.textMuted}}>Exchanges: {exch}</div>
                      </div>
                      {sel&&<span style={{fontSize:11,color:C.accent,fontWeight:700}}>✓</span>}
                    </label>
                  );
                })}
              </div>
              <div style={{fontSize:11,color:C.textMuted,marginTop:4}}>Selected: <span style={{color:C.accent,fontFamily:"monospace"}}>{rForm.products||"none"}</span></div>
            </div>
            {rErr&&<div style={{background:`${C.red}15`,border:`1px solid ${C.red}44`,borderRadius:6,padding:"8px 12px",fontSize:12,color:C.red,marginBottom:12}}>{rErr}</div>}
            <button style={{...btn("primary"),width:"100%",padding:"11px"}} onClick={addRule}>➕ Add Rule</button>
          </div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:12}}>Active Rules ({rules.length})</div>
            {rules.length===0
              ?<div style={{background:C.panel,border:`2px dashed ${C.border}`,borderRadius:12,padding:32,textAlign:"center",color:C.textMuted,fontSize:13}}>No rules yet.<br/>Select a master + children and add a rule.</div>
              :rules.map(r=>{
                const master=masters.find(m=>m.id===r.masterId);
                const kidNames=r.childIds.map(id=>children.find(c=>c.id===id)).filter(Boolean);
                return (
                  <div key={r.id} style={{background:C.panel,border:`1px solid ${r.active?C.green+"44":C.border}`,borderRadius:10,padding:14,marginBottom:10,opacity:r.active?1:0.6}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                      <div style={{background:`${C.yellow}22`,border:`1px solid ${C.yellow}44`,borderRadius:6,padding:"4px 10px",fontSize:12,fontWeight:700,color:C.yellow}}>👑 {master?.label||master?.uid||"?"}</div>
                      <div style={{color:C.textDim,fontSize:16}}>→</div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {kidNames.map(c=><div key={c.id} style={{background:`${C.accent}22`,border:`1px solid ${C.accent}44`,borderRadius:6,padding:"4px 10px",fontSize:12,fontWeight:600,color:C.accent}}>👶 {c.label||c.uid}</div>)}
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,fontSize:11,color:C.textDim,marginBottom:10}}>
                      <div>×{r.multiplier} multiplier</div>
                      <div>Max ₹{parseFloat(r.maxOrderValue||0).toLocaleString("en-IN")}</div>
                      <div style={{fontFamily:"monospace"}}>{r.exchanges?.join(",")} | {r.products?.map(p=>prdAlias(p)).join(",")}</div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button style={{...btn(),padding:"4px 12px",fontSize:11}} onClick={()=>setRules(rs=>rs.map(x=>x.id===r.id?{...x,active:!x.active}:x))}>{r.active?"⏸ Pause":"▶ Resume"}</button>
                      <button style={{...btn("danger"),padding:"4px 12px",fontSize:11}} onClick={()=>setRules(rs=>rs.filter(x=>x.id!==r.id))}>🗑 Delete</button>
                      <div style={{marginLeft:"auto",display:"flex",alignItems:"center"}}><span style={badge(r.active?C.green:C.textDim)}>{r.active?"ACTIVE":"PAUSED"}</span></div>
                    </div>
                  </div>
                );
              })
            }
          </div>
        </div>
      )}

      {tab==="log"&&(
        <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontSize:14,fontWeight:700,color:C.text}}>Activity Log</div>
            <button style={{...btn(),padding:"4px 12px",fontSize:12}} onClick={()=>setCtLog([])}>Clear</button>
          </div>
          <div style={{maxHeight:500,overflowY:"auto",fontFamily:"monospace",fontSize:12}}>
            {log.length===0
              ?<div style={{color:C.textMuted,textAlign:"center",padding:40}}>No activity yet. Start the engine to see logs.</div>
              :log.map((l,i)=><div key={i} style={{color:logColor[l.type]||C.text,padding:"4px 0",borderBottom:`1px solid ${C.border}18`,lineHeight:1.5}}><span style={{color:C.textMuted,marginRight:10,fontSize:10}}>{l.time}</span>{l.msg}</div>)
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ── Bulk Cancel helper (used in OrderBookTab) ────────────────────────────────
// Already integrated into OrderBookTab below via bulkCancel

// ── Basket Order Tab ──────────────────────────────────────────────────────────
const EMPTY_LEG = () => ({
  id: Date.now() + Math.random(),
  exch:"NSE", tsym:"", qty:"1", prc:"0",
  trantype:"B", prd:"I", prctyp:"LMT", ret:"DAY", remarks:""
});

function BasketOrderTab({uid, actid, token, userDetails}) {
  const [legs, setLegs]       = useState([EMPTY_LEG()]);
  const [results, setResults] = useState({});   // id → {stat, norenordno, emsg}
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState({});   // id → query
  const [searchRes, setSearchRes] = useState({});
  const prarr = userDetails?.prarr || [];

  const setLeg = (id, k, v) => setLegs(ls => ls.map(l => l.id===id ? {...l,[k]:v} : l));
  const addLeg = () => setLegs(ls => [...ls, EMPTY_LEG()]);
  const removeLeg = id => setLegs(ls => ls.filter(l => l.id!==id));

  async function doSearch(id, q) {
    if(!q.trim()) return;
    const r = await searchScrip(uid, q, token, legs.find(l=>l.id===id)?.exch);
    setSearchRes(s => ({...s, [id]: r?.values||[]}));
  }

  async function executeBasket() {
    if(legs.length===0){return;}
    setLoading(true); setResults({});
    // Execute all legs in parallel
    const tasks = legs.map(async leg => {
      if(!leg.tsym.trim()) return {id:leg.id, stat:"Not_Ok", emsg:"Symbol required"};
      const params = {
        uid, actid, exch:leg.exch, tsym:leg.tsym.trim(),
        qty:String(leg.qty), prc:String(leg.prc),
        prd:leg.prd, trantype:leg.trantype,
        prctyp:leg.prctyp, ret:leg.ret,
        remarks:leg.remarks||"Basket", ordersource:"API"
      };
      const res = await placeOrder(params, token);
      return {id:leg.id, ...res};
    });
    const allRes = await Promise.all(tasks);
    const map = {};
    allRes.forEach(r => { map[r.id] = r; });
    setResults(map);
    setLoading(false);
  }

  const allDone  = legs.length > 0 && legs.every(l => results[l.id]);
  const allOk    = allDone && legs.every(l => results[l.id]?.stat==="Ok");
  const successN = Object.values(results).filter(r=>r.stat==="Ok").length;
  const failN    = Object.values(results).filter(r=>r.stat!=="Ok").length;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:16,fontWeight:700,color:C.text}}>Basket Order</div>
          <div style={{fontSize:12,color:C.textDim,marginTop:2}}>Place multiple orders simultaneously across different symbols.</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button style={{...btn(),padding:"7px 14px",fontSize:13}} onClick={addLeg}>+ Add Leg</button>
          <button
            style={{...btn("primary"),padding:"7px 18px",fontSize:13,fontWeight:700}}
            onClick={executeBasket} disabled={loading||legs.length===0}>
            {loading ? "⏳ Executing…" : `▶ Execute ${legs.length} Leg${legs.length!==1?"s":""}`}
          </button>
        </div>
      </div>

      {/* Summary after execution */}
      {allDone && (
        <div style={{padding:"12px 16px",borderRadius:8,marginBottom:16,fontSize:13,fontWeight:600,
          background:allOk?`${C.green}18`:`${C.yellow}18`,
          border:`1px solid ${allOk?C.green:C.yellow}44`,
          color:allOk?C.green:C.yellow}}>
          {allOk ? `✅ All ${successN} orders placed successfully!`
                 : `⚠ ${successN} succeeded, ${failN} failed — check results below.`}
        </div>
      )}

      {/* Legs */}
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {legs.map((leg, idx) => {
          const res = results[leg.id];
          return (
            <div key={leg.id} style={{
              background:C.panel,
              border:`1px solid ${res ? (res.stat==="Ok"?C.green:C.red)+"66" : C.border}`,
              borderRadius:12, padding:16, position:"relative"
            }}>
              {/* Leg header */}
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <div style={{width:24,height:24,borderRadius:"50%",background:C.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#000",flexShrink:0}}>{idx+1}</div>
                <div style={{fontSize:13,fontWeight:700,color:C.text,flex:1}}>Leg {idx+1} {leg.tsym && `— ${leg.tsym}`}</div>
                {res && (
                  <div style={{fontSize:12,fontWeight:700,color:res.stat==="Ok"?C.green:C.red}}>
                    {res.stat==="Ok" ? `✅ ${res.norenordno}` : `❌ ${res.emsg||"Failed"}`}
                  </div>
                )}
                {legs.length>1&&!loading&&(
                  <button onClick={()=>removeLeg(leg.id)}
                    style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:16,padding:"0 4px"}}>✕</button>
                )}
              </div>

              {/* Symbol search */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={lbl}>Search Symbol</label>
                  <div style={{display:"flex",gap:6}}>
                    <input style={inp} placeholder="e.g. RELIANCE" value={search[leg.id]||""}
                      onChange={e=>setSearch(s=>({...s,[leg.id]:e.target.value}))}
                      onKeyDown={e=>e.key==="Enter"&&doSearch(leg.id,search[leg.id]||"")}/>
                    <button style={{...btn(),whiteSpace:"nowrap",padding:"8px 12px"}} onClick={()=>doSearch(leg.id,search[leg.id]||"")}>Search</button>
                  </div>
                  {searchRes[leg.id]?.length>0&&(
                    <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,marginTop:4,maxHeight:120,overflowY:"auto",position:"absolute",zIndex:10,width:"calc(100% - 32px)"}}>
                      {searchRes[leg.id].map((r,i)=>(
                        <div key={i} style={{padding:"7px 12px",cursor:"pointer",fontSize:13,color:C.text,borderBottom:`1px solid ${C.border}20`}}
                          onClick={()=>{setLeg(leg.id,"tsym",r.tsym);setLeg(leg.id,"exch",r.exch);setSearchRes(s=>({...s,[leg.id]:[]}));setSearch(s=>({...s,[leg.id]:r.tsym}));}}
                          onMouseEnter={e=>e.currentTarget.style.background=C.border}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <strong>{r.tsym}</strong> <span style={{fontSize:11,color:C.textDim}}>{r.exch}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label style={lbl}>Exchange</label>
                  <select style={{...inp,appearance:"none"}} value={leg.exch} onChange={e=>setLeg(leg.id,"exch",e.target.value)}>
                    {["NSE","BSE","NFO","BFO","MCX","CDS"].map(e=><option key={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Symbol</label>
                  <input style={inp} value={leg.tsym} onChange={e=>setLeg(leg.id,"tsym",e.target.value)} placeholder="RELIANCE-EQ"/>
                </div>
                <div>
                  <label style={lbl}>Transaction</label>
                  <select style={{...inp,appearance:"none"}} value={leg.trantype} onChange={e=>setLeg(leg.id,"trantype",e.target.value)}>
                    <option value="B">BUY</option><option value="S">SELL</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Product</label>
                  <select style={{...inp,appearance:"none"}} value={leg.prd} onChange={e=>setLeg(leg.id,"prd",e.target.value)}>
                    {prarr.length>0 ? prarr.map(p=><option key={p.prd} value={p.prd}>{p.s_prdt_ali}</option>)
                      : [{prd:"I",n:"MIS"},{prd:"C",n:"CNC"},{prd:"M",n:"NRML"}].map(p=><option key={p.prd} value={p.prd}>{p.n}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Price Type</label>
                  <select style={{...inp,appearance:"none"}} value={leg.prctyp} onChange={e=>setLeg(leg.id,"prctyp",e.target.value)}>
                    <option value="LMT">LMT</option><option value="MKT">MKT</option><option value="SL-LMT">SL-LMT</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Quantity</label>
                  <input style={inp} type="number" min="1" value={leg.qty} onChange={e=>setLeg(leg.id,"qty",e.target.value)}/>
                </div>
                <div>
                  <label style={lbl}>Price (₹)</label>
                  <input style={inp} type="number" min="0" step="0.05" value={leg.prc} onChange={e=>setLeg(leg.id,"prc",e.target.value)}/>
                </div>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={lbl}>Remarks</label>
                  <input style={inp} value={leg.remarks} onChange={e=>setLeg(leg.id,"remarks",e.target.value)} placeholder="Optional…"/>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {legs.length===0&&(
        <div style={{background:C.panel,border:`2px dashed ${C.border}`,borderRadius:12,padding:40,textAlign:"center",color:C.textMuted}}>
          No legs. Click <strong>+ Add Leg</strong> to start building your basket.
        </div>
      )}
    </div>
  );
}

// ── Order Slicer Tab ──────────────────────────────────────────────────────────
function OrderSlicerTab({uid, actid, token, userDetails}) {
  const [form, setForm]       = useState({exch:"NSE",tsym:"",totalQty:"100",slices:"5",prc:"0",trantype:"B",prd:"I",prctyp:"LMT",ret:"DAY",remarks:"Slice",interval:"500"});
  const [search, setSearch]   = useState("");
  const [searchRes, setSearchRes] = useState([]);
  const [preview, setPreview] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg]         = useState(null);
  const prarr = userDetails?.prarr || [];
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  function buildPreview() {
    const total  = parseInt(form.totalQty)||0;
    const slices = parseInt(form.slices)||1;
    if(!total||!slices||!form.tsym){setMsg({type:"warn",text:"Fill symbol, total qty and slices first."});return;}
    const base = Math.floor(total/slices);
    const rem  = total % slices;
    const list = Array.from({length:slices}, (_,i)=>({
      slice: i+1,
      qty: base + (i===slices-1 ? rem : 0),
      status:"pending"
    }));
    setPreview(list); setResults([]); setMsg(null);
  }

  async function executeSlices() {
    if(!preview.length){setMsg({type:"warn",text:"Build preview first."});return;}
    if(!window.confirm(`Place ${preview.length} sliced orders for ${form.tsym}?`))return;
    setLoading(true); setMsg(null);
    const interval = parseInt(form.interval)||500;
    const res = [];
    for(let i=0;i<preview.length;i++){
      const slice = preview[i];
      const params = {
        uid, actid, exch:form.exch, tsym:form.tsym.trim(),
        qty:String(slice.qty), prc:String(form.prc),
        prd:form.prd, trantype:form.trantype,
        prctyp:form.prctyp, ret:form.ret,
        remarks:`${form.remarks||"Slice"} ${i+1}/${preview.length}`,
        ordersource:"API"
      };
      const r = await placeOrder(params, token);
      res.push({...slice, ...r});
      setResults([...res]);
      if(i < preview.length-1) await new Promise(ok=>setTimeout(ok, interval));
    }
    const ok  = res.filter(r=>r.stat==="Ok").length;
    const err = res.filter(r=>r.stat!=="Ok").length;
    setMsg({type:ok===res.length?"success":"warn", text:`✅ ${ok} placed${err?`, ❌ ${err} failed`:""}`});
    setLoading(false);
  }

  async function doSearch(){
    if(!search.trim())return;
    const r = await searchScrip(uid,search,token,form.exch);
    setSearchRes(r?.values||[]);
  }

  const totalQty = parseInt(form.totalQty)||0;
  const slices   = parseInt(form.slices)||1;
  const base     = Math.floor(totalQty/slices);
  const rem      = totalQty%slices;

  return (
    <div>
      <div style={{marginBottom:16}}>
        <div style={{fontSize:16,fontWeight:700,color:C.text}}>Order Slicer</div>
        <div style={{fontSize:12,color:C.textDim,marginTop:2}}>Split a large order into multiple smaller orders to reduce market impact.</div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
        {/* Config panel */}
        <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:14}}>Configure Slices</div>

          {/* Symbol search */}
          <div style={{marginBottom:12}}>
            <label style={lbl}>Search Symbol</label>
            <div style={{display:"flex",gap:6}}>
              <input style={inp} placeholder="e.g. NIFTY28JUL26F" value={search}
                onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doSearch()}/>
              <button style={{...btn(),padding:"8px 12px"}} onClick={doSearch}>Search</button>
            </div>
            {searchRes.length>0&&(
              <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,marginTop:4,maxHeight:120,overflowY:"auto"}}>
                {searchRes.map((r,i)=>(
                  <div key={i} style={{padding:"7px 12px",cursor:"pointer",fontSize:13,color:C.text,borderBottom:`1px solid ${C.border}20`}}
                    onClick={()=>{setForm(f=>({...f,tsym:r.tsym,exch:r.exch}));setSearch(r.tsym);setSearchRes([]);}}
                    onMouseEnter={e=>e.currentTarget.style.background=C.border}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <strong>{r.tsym}</strong> <span style={{fontSize:11,color:C.textDim}}>{r.exch}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <div><label style={lbl}>Exchange</label>
              <select style={{...inp,appearance:"none"}} value={form.exch} onChange={set("exch")}>
                {["NSE","BSE","NFO","BFO","MCX","CDS"].map(e=><option key={e}>{e}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Symbol</label>
              <input style={inp} value={form.tsym} onChange={set("tsym")} placeholder="RELIANCE-EQ"/>
            </div>
            <div><label style={lbl}>Transaction</label>
              <select style={{...inp,appearance:"none"}} value={form.trantype} onChange={set("trantype")}>
                <option value="B">BUY</option><option value="S">SELL</option>
              </select>
            </div>
            <div><label style={lbl}>Product</label>
              <select style={{...inp,appearance:"none"}} value={form.prd} onChange={set("prd")}>
                {prarr.length>0 ? prarr.map(p=><option key={p.prd} value={p.prd}>{p.s_prdt_ali}</option>)
                  : [{prd:"I",n:"MIS"},{prd:"C",n:"CNC"},{prd:"M",n:"NRML"}].map(p=><option key={p.prd} value={p.prd}>{p.n}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Price Type</label>
              <select style={{...inp,appearance:"none"}} value={form.prctyp} onChange={set("prctyp")}>
                <option value="LMT">LMT</option><option value="MKT">MKT</option><option value="SL-LMT">SL-LMT</option>
              </select>
            </div>
            <div><label style={lbl}>Price (₹)</label>
              <input style={inp} type="number" min="0" step="0.05" value={form.prc} onChange={set("prc")}/>
            </div>
          </div>

          {/* Slice settings */}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:14,marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:700,color:C.accent,marginBottom:10}}>⚡ Slice Settings</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div>
                <label style={lbl}>Total Quantity</label>
                <input style={inp} type="number" min="1" value={form.totalQty} onChange={set("totalQty")}/>
              </div>
              <div>
                <label style={lbl}>Number of Slices</label>
                <input style={inp} type="number" min="1" max="50" value={form.slices} onChange={set("slices")}/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={lbl}>Interval Between Orders (ms)</label>
                <input style={inp} type="number" min="100" step="100" value={form.interval} onChange={set("interval")}/>
                <div style={{fontSize:11,color:C.textMuted,marginTop:3}}>500ms recommended. Lower = faster but may hit rate limits.</div>
              </div>
            </div>
            {/* Live preview math */}
            {totalQty>0&&slices>0&&(
              <div style={{marginTop:10,padding:"8px 12px",background:C.panel,borderRadius:6,fontSize:12,color:C.textDim}}>
                {base} qty × {rem>0?slices-1:slices} slices{rem>0?` + ${base+rem} qty × 1 slice (remainder)`:""} = <strong style={{color:C.text}}>{totalQty} total</strong>
              </div>
            )}
          </div>

          <div style={{marginBottom:12}}>
            <label style={lbl}>Remarks</label>
            <input style={inp} value={form.remarks} onChange={set("remarks")} placeholder="Slice"/>
          </div>

          {msg&&<div style={{padding:"9px 14px",borderRadius:6,fontSize:12,fontWeight:600,marginBottom:12,
            color:msg.type==="success"?C.green:msg.type==="warn"?C.yellow:C.red,
            background:msg.type==="success"?`${C.green}18`:msg.type==="warn"?`${C.yellow}18`:`${C.red}18`,
            border:`1px solid ${msg.type==="success"?C.green:msg.type==="warn"?C.yellow:C.red}44`}}>{msg.text}</div>}

          <div style={{display:"flex",gap:8}}>
            <button style={{...btn(),flex:1,padding:"10px"}} onClick={buildPreview}>Preview Slices</button>
            <button style={{...btn("primary"),flex:1,padding:"10px",fontWeight:700}}
              onClick={executeSlices} disabled={loading||!preview.length}>
              {loading?"⏳ Slicing…":"▶ Execute Slices"}
            </button>
          </div>
        </div>

        {/* Preview / Results panel */}
        <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:14}}>
            {results.length>0 ? `Execution Results (${results.length}/${preview.length})` : "Slice Preview"}
          </div>
          {preview.length===0?(
            <div style={{textAlign:"center",color:C.textMuted,padding:40,fontSize:13}}>
              Fill the form and click <strong>Preview Slices</strong> to see how your order will be split.
            </div>
          ):(
            <div>
              {/* Progress bar when loading */}
              {loading&&(
                <div style={{marginBottom:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:C.textDim,marginBottom:4}}>
                    <span>Executing…</span><span>{results.length}/{preview.length}</span>
                  </div>
                  <div style={{background:C.border,borderRadius:4,height:6}}>
                    <div style={{background:C.accent,borderRadius:4,height:6,width:`${(results.length/preview.length)*100}%`,transition:"width 0.3s"}}/>
                  </div>
                </div>
              )}
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr>
                    {["Slice","Qty","Status","Order No"].map(c=><th key={c} style={th}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((s,i)=>{
                    const r = results[i];
                    return (
                      <tr key={i} style={{background:i%2===0?"transparent":`${C.surface}60`}}>
                        <td style={{...td,fontWeight:700,color:C.accent}}>#{s.slice}</td>
                        <td style={td}>{s.qty}</td>
                        <td style={td}>
                          {!r ? <span style={{color:C.textMuted,fontSize:12}}>Pending</span>
                          : r.stat==="Ok" ? <span style={{color:C.green,fontWeight:700}}>✅ Placed</span>
                          : <span style={{color:C.red,fontWeight:700}} title={r.emsg}>❌ Failed</span>}
                        </td>
                        <td style={{...td,fontSize:11,color:C.textDim,fontFamily:"monospace"}}>
                          {r?.stat==="Ok" ? r.norenordno : r?.emsg ? r.emsg.slice(0,30) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Modify Order Modal ────────────────────────────────────────────────────────
function ModifyOrderModal({uid, order, token, onClose, onDone}) {
  const [form, setForm] = useState({
    qty:    order.qty    || "1",
    prc:    order.prc    || "0",
    prctyp: order.prctyp || "LMT",
    ret:    order.ret    || "DAY",
    trgprc: order.trgprc || "0",
  });
  const [msg, setMsg]       = useState(null);
  const [loading, setLoading] = useState(false);
  const set = k => e => setForm(f => ({...f, [k]: e.target.value}));
  const isSL = form.prctyp === "SL-LMT";

  async function submit() {
    setMsg(null); setLoading(true);
    const params = {
      uid,
      norenordno: order.norenordno,
      exch:       order.exch,
      tsym:       order.tsym,
      prctyp:     form.prctyp,
      prc:        String(form.prc),
      qty:        String(form.qty),
      ret:        form.ret,
      ...(isSL && parseFloat(form.trgprc) > 0 ? {trgprc: String(form.trgprc)} : {}),
    };
    const res = await modifyOrder(params, token);
    setLoading(false);
    if (res.stat === "Ok") {
      setMsg({type:"success", text:`✅ Order modified! Order No: ${res.result||order.norenordno}`});
      setTimeout(() => { onDone(); onClose(); }, 1200);
    } else {
      setMsg({type:"error", text: res.emsg || "Modify failed."});
    }
  }

  return (
    <div style={{position:"fixed",inset:0,background:"#00000090",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,fontFamily:"'Inter',sans-serif"}}>
      <div style={{width:440,background:C.panel,borderRadius:14,border:`1px solid ${C.borderHi}`,padding:24,maxHeight:"90vh",overflowY:"auto"}}>
        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:C.text}}>Modify Order</div>
            <div style={{fontSize:12,color:C.textDim,marginTop:2}}>
              {order.tsym} — {order.trantype==="B"?"BUY":"SELL"} — {order.exch}
            </div>
          </div>
          <button style={{background:"none",border:"none",color:C.textDim,fontSize:20,cursor:"pointer"}} onClick={onClose}>✕</button>
        </div>

        {/* Order info strip */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",marginBottom:16,display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,fontSize:12}}>
          <div><div style={{color:C.textMuted}}>Order No</div><div style={{color:C.text,fontFamily:"monospace",fontSize:11}}>{order.norenordno}</div></div>
          <div><div style={{color:C.textMuted}}>Status</div><StatusPill status={order.status}/></div>
          <div><div style={{color:C.textMuted}}>Filled</div><div style={{color:C.green}}>{order.fillshares||"0"} / {order.qty}</div></div>
        </div>

        {/* Editable fields */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          <div>
            <label style={lbl}>Price Type</label>
            <select style={{...inp,appearance:"none"}} value={form.prctyp} onChange={set("prctyp")}>
              <option value="LMT">LMT — Limit</option>
              <option value="SL-LMT">SL-LMT — Stop Loss Limit</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Retention</label>
            <select style={{...inp,appearance:"none"}} value={form.ret} onChange={set("ret")}>
              <option>DAY</option><option>IOC</option><option>EOS</option>
            </select>
          </div>
          <div>
            <label style={lbl}>New Quantity</label>
            <input style={inp} type="number" min="1" value={form.qty} onChange={set("qty")}/>
            <div style={{fontSize:10,color:C.textMuted,marginTop:3}}>Total qty (filled + pending)</div>
          </div>
          <div>
            <label style={lbl}>New Price (₹)</label>
            <input style={inp} type="number" min="0" step="0.05" value={form.prc} onChange={set("prc")}/>
          </div>

          {/* Trigger price for SL-LMT */}
          {isSL && (
            <div style={{gridColumn:"1/-1"}}>
              <div style={{background:`${C.yellow}12`,border:`1px solid ${C.yellow}33`,borderRadius:8,padding:"10px 14px"}}>
                <label style={{...lbl,color:C.yellow}}>Trigger Price (₹) *</label>
                <input style={{...inp,borderColor:C.yellow}} type="number" min="0" step="0.05" value={form.trgprc} onChange={set("trgprc")}/>
                <div style={{fontSize:10,color:C.textMuted,marginTop:3}}>Required for SL-LMT orders</div>
              </div>
            </div>
          )}
        </div>

        {/* Change summary */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12}}>
          <div style={{fontWeight:700,color:C.textDim,marginBottom:6,fontSize:11,textTransform:"uppercase",letterSpacing:"0.5px"}}>Change Summary</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {[
              ["Qty",    order.qty,    form.qty,    ""],
              ["Price",  `₹${order.prc}`, `₹${form.prc}`, ""],
              ...(isSL ? [["Trigger", `₹${order.trgprc||"0"}`, `₹${form.trgprc}`, ""]] : []),
            ].map(([label, oldVal, newVal]) => (
              <div key={label} style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{color:C.textMuted,minWidth:40}}>{label}:</span>
                <span style={{color:C.textDim,textDecoration:"line-through"}}>{oldVal}</span>
                <span style={{color:C.accent}}>→ {newVal}</span>
              </div>
            ))}
          </div>
        </div>

        {msg && (
          <div style={{padding:"10px 14px",borderRadius:6,marginBottom:12,fontSize:13,
            color:msg.type==="success"?C.green:C.red,
            background:msg.type==="success"?`${C.green}18`:`${C.red}18`,
            border:`1px solid ${msg.type==="success"?C.green:C.red}44`}}>
            {msg.text}
          </div>
        )}

        <div style={{display:"flex",gap:8}}>
          <button style={{...btn("primary"),flex:1,padding:"11px",fontSize:14,fontWeight:700}}
            onClick={submit} disabled={loading}>
            {loading ? "⏳ Modifying…" : "✏️ Modify Order"}
          </button>
          <button style={{...btn(),flex:1,padding:"11px"}} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}


// ── Place Order Modal ─────────────────────────────────────────────────────────
function PlaceOrderModal({uid,actid,token,userDetails,onClose}) {
  const [form,setForm]=useState({
    exch:"NSE",tsym:"",qty:"1",prc:"0",prd:"I",trantype:"B",
    prctyp:"LMT",ret:"DAY",remarks:"",
    trgprc:"0",   // Trigger price — SL-LMT
    blprc:"0",    // Book Loss price — BO (B)
    bpprc:"0",    // Book Profit price — BO (B)  / target
    trailprc:"0", // Trailing price — BO optional
  });
  const [search,setSearch]=useState("");
  const [results,setResults]=useState([]);
  const [msg,setMsg]=useState(null);
  const set=k=>e=>setForm(f=>({...f,[k]:e.target.value}));

  async function doSearch(){if(!search.trim())return;const r=await searchScrip(uid,search,token,form.exch);setResults(r?.values||[]);}
  function selectScrip(s){setForm(f=>({...f,exch:s.exch,tsym:s.tsym}));setResults([]);setSearch(s.tsym);}

  // Derived flags
  const isBO     = form.prd==="B";   // Bracket Order — needs blprc (stop loss) + bpprc (target)
  const isCO     = form.prd==="H";   // Cover Order (product H) — needs blprc (stop loss)
  const isSLLMT  = form.prctyp==="SL-LMT"; // SL-LMT — needs trgprc

  async function submit(){
    setMsg(null);
    if(!form.tsym.trim()){setMsg({type:"error",text:"Select a symbol first."});return;}
    if(isBO&&(!parseFloat(form.blprc)||!parseFloat(form.bpprc))){
      setMsg({type:"error",text:"Bracket Order requires Stop Loss price and Target price."});return;
    }
    if(isCO&&!parseFloat(form.blprc)){
      setMsg({type:"error",text:"Cover Order requires a Stop Loss price."});return;
    }
    if(isSLLMT&&!parseFloat(form.trgprc)){
      setMsg({type:"error",text:"SL-LMT requires a Trigger price."});return;
    }
    const params={
      uid,actid,
      exch:    form.exch,
      tsym:    form.tsym.trim(),
      qty:     String(form.qty),
      prc:     String(form.prc),
      prd:     form.prd,
      trantype:form.trantype,
      prctyp:  form.prctyp,
      ret:     form.ret,
      remarks: form.remarks||"",
      ordersource:"API",
      ...(isSLLMT&&parseFloat(form.trgprc)>0 ? {trgprc:String(form.trgprc)} : {}),
      ...(isBO ? {
        blprc:    String(form.blprc),
        bpprc:    String(form.bpprc),
        ...(parseFloat(form.trailprc)>0 ? {trailprc:String(form.trailprc)} : {}),
      } : {}),
      ...(isCO ? {blprc:String(form.blprc)} : {}),
    };
    const res=await placeOrder(params,token);
    if(res.stat==="Ok") setMsg({type:"success",text:"✅ Order placed! Order No: "+res.norenordno});
    else                setMsg({type:"error",  text:res.emsg||"Order failed."});
  }

  const prarr=userDetails?.prarr||[];

  return (
    <div style={{position:"fixed",inset:0,background:"#00000090",display:"flex",alignItems:"center",justifyContent:"center",zIndex:100,fontFamily:"'Inter',sans-serif"}}>
      <div style={{width:520,background:C.panel,borderRadius:14,border:`1px solid ${C.borderHi}`,padding:24,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:16,fontWeight:700,color:C.text}}>Place Order</div>
          <button style={{background:"none",border:"none",color:C.textDim,fontSize:20,cursor:"pointer"}} onClick={onClose}>✕</button>
        </div>

        {/* Symbol search */}
        <div style={{marginBottom:14}}>
          <label style={lbl}>Search Symbol</label>
          <div style={{display:"flex",gap:8}}>
            <input style={inp} placeholder="e.g. RELIANCE-EQ" value={search}
              onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doSearch()}/>
            <button style={{...btn(),whiteSpace:"nowrap"}} onClick={doSearch}>Search</button>
          </div>
          {results.length>0&&(
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,marginTop:4,maxHeight:150,overflowY:"auto"}}>
              {results.map((r,i)=>(
                <div key={i} style={{padding:"8px 12px",cursor:"pointer",borderBottom:`1px solid ${C.border}20`,color:C.text,fontSize:13}}
                  onClick={()=>selectScrip(r)}
                  onMouseEnter={e=>e.currentTarget.style.background=C.border}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <strong>{r.tsym}</strong> <span style={{color:C.textDim,fontSize:11}}>{r.exch}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Core fields */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          <div><label style={lbl}>Exchange</label>
            <select style={{...inp,appearance:"none"}} value={form.exch} onChange={set("exch")}>
              {["NSE","BSE","NFO","BFO","MCX","CDS"].map(e=><option key={e}>{e}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Symbol</label>
            <input style={inp} value={form.tsym} onChange={set("tsym")} placeholder="RELIANCE-EQ"/>
          </div>
          <div><label style={lbl}>Transaction</label>
            <select style={{...inp,appearance:"none"}} value={form.trantype} onChange={set("trantype")}>
              <option value="B">BUY</option><option value="S">SELL</option>
            </select>
          </div>
          <div><label style={lbl}>Product</label>
            <select style={{...inp,appearance:"none"}} value={form.prd} onChange={set("prd")}>
              {prarr.length>0
                ? prarr.map(p=><option key={p.prd} value={p.prd}>{p.s_prdt_ali} ({p.prd})</option>)
                : PRODUCTS.map(p=><option key={p.prd} value={p.prd}>{p.alias} — {p.label.split("—")[1]||p.label}</option>)
              }
            </select>
          </div>
          <div><label style={lbl}>Price Type</label>
            <select style={{...inp,appearance:"none"}} value={form.prctyp} onChange={set("prctyp")}>
              <option value="LMT">LMT — Limit</option>
              <option value="MKT">MKT — Market</option>
              <option value="SL-LMT">SL-LMT — Stop Loss Limit</option>
              <option value="DS">DS — Day Special</option>
            </select>
          </div>
          <div><label style={lbl}>Retention</label>
            <select style={{...inp,appearance:"none"}} value={form.ret} onChange={set("ret")}>
              <option>DAY</option><option>IOC</option><option>EOS</option>
            </select>
          </div>
          <div><label style={lbl}>Quantity</label>
            <input style={inp} type="number" min="1" value={form.qty} onChange={set("qty")}/>
          </div>
          <div><label style={lbl}>Price (₹)</label>
            <input style={inp} type="number" min="0" step="0.05" value={form.prc} onChange={set("prc")}/>
          </div>
        </div>

        {/* ── SL-LMT: Trigger Price ── */}
        {isSLLMT&&(
          <div style={{background:`${C.yellow}12`,border:`1px solid ${C.yellow}33`,borderRadius:8,padding:14,marginBottom:14}}>
            <div style={{fontSize:12,fontWeight:700,color:C.yellow,marginBottom:10}}>🎯 Stop Loss Settings</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div>
                <label style={lbl}>Trigger Price (₹) *</label>
                <input style={{...inp,borderColor:C.yellow}} type="number" min="0" step="0.05"
                  value={form.trgprc} onChange={set("trgprc")}/>
                <div style={{fontSize:10,color:C.textMuted,marginTop:3}}>Order triggers when price hits this level</div>
              </div>
              <div style={{display:"flex",alignItems:"flex-end",paddingBottom:4}}>
                <div style={{fontSize:11,color:C.textDim,lineHeight:1.6}}>
                  Trigger: <strong style={{color:C.yellow}}>₹{form.trgprc}</strong><br/>
                  Limit: <strong style={{color:C.text}}>₹{form.prc}</strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Cover Order: Stop Loss ── */}
        {isCO&&(
          <div style={{background:`${C.red}10`,border:`1px solid ${C.red}33`,borderRadius:8,padding:14,marginBottom:14}}>
            <div style={{fontSize:12,fontWeight:700,color:C.red,marginBottom:10}}>🛡 Cover Order — Stop Loss</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div>
                <label style={lbl}>Stop Loss Price (blprc) (₹) *</label>
                <input style={{...inp,borderColor:C.red}} type="number" min="0" step="0.05"
                  value={form.blprc} onChange={set("blprc")}/>
                <div style={{fontSize:10,color:C.textMuted,marginTop:3}}>Mandatory for Cover Orders</div>
              </div>
              <div style={{display:"flex",alignItems:"flex-end",paddingBottom:4}}>
                <div style={{fontSize:11,color:C.textDim,lineHeight:1.6}}>
                  Entry: <strong style={{color:C.text}}>₹{form.prc}</strong><br/>
                  Stop Loss: <strong style={{color:C.red}}>₹{form.blprc}</strong><br/>
                  Risk: <strong style={{color:C.red}}>
                    ₹{Math.abs(parseFloat(form.prc||0)-parseFloat(form.blprc||0)).toFixed(2)}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Bracket Order: Target + Stop Loss + Trailing ── */}
        {isBO&&(
          <div style={{background:`${C.accent}10`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:14,marginBottom:14}}>
            <div style={{fontSize:12,fontWeight:700,color:C.accent,marginBottom:10}}>📊 Bracket Order — Target & Stop Loss</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div>
                <label style={lbl}>Target Price (bpprc) (₹) *</label>
                <input style={{...inp,borderColor:C.green}} type="number" min="0" step="0.05"
                  value={form.bpprc} onChange={set("bpprc")}/>
                <div style={{fontSize:10,color:C.textMuted,marginTop:3}}>Book profit when price hits target</div>
              </div>
              <div>
                <label style={lbl}>Stop Loss Price (blprc) (₹) *</label>
                <input style={{...inp,borderColor:C.red}} type="number" min="0" step="0.05"
                  value={form.blprc} onChange={set("blprc")}/>
                <div style={{fontSize:10,color:C.textMuted,marginTop:3}}>Book loss to limit downside</div>
              </div>
              <div>
                <label style={lbl}>Trailing Stop (trailprc) (₹)</label>
                <input style={inp} type="number" min="0" step="0.05"
                  value={form.trailprc} onChange={set("trailprc")}/>
                <div style={{fontSize:10,color:C.textMuted,marginTop:3}}>Optional — trailing stop loss amount</div>
              </div>
              <div style={{display:"flex",alignItems:"center"}}>
                <div style={{fontSize:11,color:C.textDim,lineHeight:1.8,background:C.surface,borderRadius:6,padding:"8px 10px",width:"100%"}}>
                  Entry: <strong style={{color:C.text}}>₹{form.prc}</strong><br/>
                  Target: <strong style={{color:C.green}}>₹{form.bpprc}</strong>
                  {parseFloat(form.bpprc)>0&&parseFloat(form.prc)>0&&(
                    <span style={{color:C.green}}> (+₹{Math.abs(parseFloat(form.bpprc)-parseFloat(form.prc)).toFixed(2)})</span>
                  )}<br/>
                  Stop Loss: <strong style={{color:C.red}}>₹{form.blprc}</strong>
                  {parseFloat(form.blprc)>0&&parseFloat(form.prc)>0&&(
                    <span style={{color:C.red}}> (-₹{Math.abs(parseFloat(form.prc)-parseFloat(form.blprc)).toFixed(2)})</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remarks */}
        <div style={{marginBottom:14}}>
          <label style={lbl}>Remarks</label>
          <input style={inp} value={form.remarks} onChange={set("remarks")} placeholder="Optional…"/>
        </div>

        {/* Result message */}
        {msg&&(
          <div style={{padding:"10px 14px",borderRadius:6,marginBottom:12,fontSize:13,
            color:msg.type==="success"?C.green:C.red,
            background:msg.type==="success"?`${C.green}18`:`${C.red}18`,
            border:`1px solid ${msg.type==="success"?C.green:C.red}44`}}>
            {msg.text}
          </div>
        )}

        <div style={{display:"flex",gap:8}}>
          <button style={{...btn(form.trantype==="B"?"green":"danger"),flex:1,padding:"11px",fontSize:14,fontWeight:700}} onClick={submit}>
            {form.trantype==="B"?"▲ Buy":"▼ Sell"} {form.tsym||"—"}
            {isBO&&" (BO)"}{isCO&&" (CO)"}
          </button>
          <button style={{...btn(),flex:1,padding:"11px"}} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Root App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [page,setPage]=useState("credentials");
  const [creds,setCreds]=useState({clientId:"",secretKey:""});
  const [authToken,setAuthToken]=useState(null);
  const [userData,setUserData]=useState(null);
  const [userDetails,setUserDetails]=useState(null);
  const [activeTab,setActiveTab]=useState("copy");
  const [showOrder,setShowOrder]=useState(false);
  const [selectedAccIdx, setSelectedAccIdx] = useState(0); // account switcher

  // ── Copy Engine state — lives here so it survives tab switches ──
  const [ctMasters, setCtMasters] =useState(()=>restore(SK_MASTERS, []));
  const [ctChildren,setCtChildren]=useState(()=>restore(SK_CHILDREN,[]));
  const [ctRules,   setCtRules]   =useState(()=>restore(SK_RULES,   []));
  const [ctRunning, setCtRunning] =useState(false);
  const [ctLog,     setCtLog]     =useState([]);
  const ctIRef      = useRef(null);
  const ctPrevRef   = useRef({});
  // Refs always hold the LATEST state — solves stale closure in setInterval
  const ctMastersRef  = useRef([]);
  const ctChildrenRef = useRef([]);
  const ctRulesRef    = useRef([]);
  const ctAuthRef     = useRef({uid:"", actid:"", token:null});

  useEffect(() => { ctMastersRef.current  = ctMasters;  }, [ctMasters]);
  useEffect(() => { ctChildrenRef.current = ctChildren; }, [ctChildren]);
  useEffect(() => { ctRulesRef.current    = ctRules;    }, [ctRules]);

  useEffect(()=>{persist(SK_MASTERS, ctMasters); },[ctMasters]);
  useEffect(()=>{persist(SK_CHILDREN,ctChildren);},[ctChildren]);
  useEffect(()=>{persist(SK_RULES,   ctRules);   },[ctRules]);

  const ctAddLog=useCallback((msg,type="info")=>setCtLog(l=>[{msg,type,time:new Date().toLocaleTimeString("en-IN")},...l].slice(0,500)),[]);

  async function ctCopyOrder(order,child,multiplier,maxOrderValue) {
    const qty=Math.max(1,Math.round(parseInt(order.qty||1)*parseFloat(multiplier||1)));
    const val=qty*parseFloat(order.prc||0);
    if(parseFloat(maxOrderValue)>0&&val>parseFloat(maxOrderValue)){ctAddLog(`⚠ Skipped ${order.tsym} for ${child.label||child.uid}: ₹${val.toFixed(0)} > max ₹${maxOrderValue}`,"warn");return;}
    const side=order.trantype==="B"?"BUY":"SELL";
    const params={uid:child.uid,actid:child.actid,exch:order.exch,tsym:order.tsym,qty:qty.toString(),prc:order.prc||"0",prd:order.prd,trantype:order.trantype,prctyp:order.prctyp||"LMT",ret:order.ret||"DAY",remarks:"CopyTrade:"+order.norenordno,ordersource:"API",...(order.trgprc&&parseFloat(order.trgprc)>0?{trgprc:order.trgprc}:{})};
    try{
      const res=await apiPost("/PlaceOrder",params,child.access_token);
      if(res.stat==="Ok"){
        ctAddLog(`✅ ${child.label||child.uid}: ${side} ${order.tsym} ×${qty} @ ₹${order.prc} → Order ${res.norenordno}`,"success");
      } else {
        const emsg = res.emsg || "Unknown error";
        if(emsg.toLowerCase().includes("session")||emsg.toLowerCase().includes("invalid session")){
          ctAddLog(`❌ ${child.label||child.uid}: TOKEN EXPIRED — Update token in Child Accounts tab!`,"error");
        } else {
          ctAddLog(`❌ ${child.label||child.uid}: ${emsg}`,"error");
        }
      }
    }catch(e){ctAddLog(`❌ ${child.label||child.uid}: ${e.message}`,"error");}
  }

  async function ctPoll() {
    // Always read latest values from refs — avoids stale closure bug
    const rules  = ctRulesRef.current;
    const masters= ctMastersRef.current;
    const kids   = ctChildrenRef.current;
    for(const rule of rules){
      if(!rule.active)continue;
      const master=masters.find(m=>m.id===rule.masterId);
      if(!master)continue;
      const linkedKids=kids.filter(c=>rule.childIds.includes(c.id));
      if(!linkedKids.length)continue;
      try{
        // If master uid matches logged-in user OR token is __SESSION__, use session token
        const { uid: sessionUid, actid: sessionActid, token: sessionToken } = ctAuthRef.current;
        const masterToken = (master.access_token === "__SESSION__" || master.uid === sessionUid || master.uid === sessionActid)
          ? sessionToken : master.access_token;
        const orders=await apiPost("/OrderBook",{uid:master.uid},masterToken);
        if(!Array.isArray(orders)){
          const emsg = orders?.emsg || JSON.stringify(orders);
          if(emsg.toLowerCase().includes("session") || emsg.toLowerCase().includes("invalid")) {
            ctAddLog(`❌ MASTER ${master.label||master.uid}: Token expired! — "${emsg}" — Update token and restart engine.`,"error");
          } else {
            ctAddLog(`⚠ OrderBook error for ${master.label||master.uid}: ${emsg}`,"warn");
          }
          continue;
        }
        const prev=ctPrevRef.current[rule.id]||{};
        const toProcess=[];
        for(const o of orders){
          if(prev[o.norenordno])continue;
          ctAddLog(`👁 ${o.tsym} ${o.trantype==="B"?"BUY":"SELL"} ${o.qty}@${o.prc} exch:${o.exch} prd:${o.prd} status:${o.status}`);
          if(o.status==="CANCELED"){ctAddLog(`   ⏭ CANCELED — skipped`,"warn");continue;}
          if((o.remarks||"").startsWith("CopyTrade:")){ctAddLog(`   ⏭ Already a copy — skipped`,"warn");continue;}
          if(!rule.exchanges.includes(o.exch)){ctAddLog(`   ⏭ Exchange ${o.exch} not in rule [${rule.exchanges.join(",")}]`,"warn");continue;}
          if(!rule.products.includes(o.prd)){ctAddLog(`   ⏭ Product ${o.prd} not in rule [${rule.products.join(",")}]`,"warn");continue;}
          toProcess.push(o);
        }
        if(toProcess.length){
          ctAddLog(`🔍 ${toProcess.length} order(s) to copy → ${linkedKids.length} child(ren)`);
          // Copy to ALL children concurrently (parallel) — reduces delay
          const copyTasks = [];
          for(const order of toProcess)
            for(const child of linkedKids)
              copyTasks.push(ctCopyOrder(order,child,rule.multiplier,rule.maxOrderValue));
          await Promise.all(copyTasks);
        }
        const map={};orders.forEach(o=>{map[o.norenordno]=true;});
        ctPrevRef.current[rule.id]=map;
      }catch(e){ctAddLog(`⚠ Poll error (${master.label||master.uid}): ${e.message}`,"error");}
    }
  }

  async function ctStart(){
    if(!ctMasters.length){ctAddLog("Add at least one master account.","error");return;}
    if(!ctChildren.length){ctAddLog("Add at least one child account.","error");return;}
    if(!ctRules.length){ctAddLog("Add at least one copy rule.","error");return;}
    // Sync refs with latest state before starting
    ctMastersRef.current  = ctMasters;
    ctChildrenRef.current = ctChildren;
    ctRulesRef.current    = ctRules;
    ctAuthRef.current     = { uid, actid, token: authToken };
    ctPrevRef.current     = {};
    setCtRunning(true);
    ctAddLog("🟢 Engine started — polling every 500ms. Runs even when you switch tabs.", "success");
    ctAddLog(`   Masters : ${ctMasters.map(m=>m.label||m.uid).join(", ")}`);
    ctAddLog(`   Children: ${ctChildren.map(c=>c.label||c.uid).join(", ")}`);
    ctAddLog(`   Rules   : ${ctRules.length} rule(s) active`);
    ctAddLog(`   Verifying child tokens…`);
    // Verify all tokens before starting
    ctAddLog("🔍 Verifying account tokens…");
    let allValid = true;
    for(const master of ctMasters){
      try{
        // Use session token if master uid matches login OR token is __SESSION__
        ctAuthRef.current = { uid, actid, token: authToken };
        const masterToken = (master.access_token === "__SESSION__" || master.uid === uid || master.uid === actid)
          ? authToken : master.access_token;
        const r=await apiPost("/OrderBook",{uid:master.uid},masterToken);
        if(Array.isArray(r)){ctAddLog(`   ✅ Master [${master.label||master.uid}]: Token valid, ${r.length} orders found`,"success");}
        else{ctAddLog(`   ❌ Master [${master.label||master.uid}]: ${r.emsg} — Please logout and login again!`,"error"); allValid=false;}
      }catch(e){ctAddLog(`   ❌ Master [${master.label||master.uid}]: ${e.message}`,"error"); allValid=false;}
    }
    for(const child of ctChildren){
      try{
        const r=await apiPost("/OrderBook",{uid:child.uid},child.access_token);
        if(Array.isArray(r)){ctAddLog(`   ✅ Child  [${child.label||child.uid}]: Token valid, ${r.length} orders found`,"success");}
        else{ctAddLog(`   ❌ Child  [${child.label||child.uid}]: ${r.emsg} — Go to Child Accounts tab and update the token!`,"error"); allValid=false;}
      }catch(e){ctAddLog(`   ❌ Child  [${child.label||child.uid}]: ${e.message}`,"error"); allValid=false;}
    }
    if(!allValid){
      ctAddLog("⛔ One or more tokens are invalid. Update them and start again.","error");
      setCtRunning(false); return;
    }
    ctAddLog("✅ All tokens valid — starting copy engine!","success");
    ctPoll();
    ctIRef.current=setInterval(ctPoll,500);
  }
  function ctStop(){clearInterval(ctIRef.current);setCtRunning(false);ctAddLog("🔴 Engine stopped.","warn");}
  async function ctSync(){ctAddLog("🔄 Manual sync…");await ctPoll();ctAddLog("✅ Sync complete.");}

  function handleCredsNext(clientId,secretKey){setCreds({clientId,secretKey});setPage("login");}
  async function handleAuth(token,loginData){
    // Normalize uid field — API returns USERID in some flows
    const normalizedData = {
      ...loginData,
      uid: loginData.uid || loginData.USERID || loginData.actid || "",
      actid: loginData.actid || loginData.uid || loginData.USERID || "",
    };
    setAuthToken(token);
    setUserData(normalizedData);
    setPage("dashboard");
    const u = normalizedData.uid;
    if(u){
      try{
        const d=await getUserDetails(u,token);
        if(d&&d.stat!=="Not_Ok") setUserDetails(d);
      }catch(e){ console.warn("UserDetails failed:", e.message); }
    }
  }
  async function handleLogout(){
    clearInterval(ctIRef.current);setCtRunning(false);
    if(userData?.uid)await logout(userData.uid,authToken);
    setAuthToken(null);setUserData(null);setUserDetails(null);
    setCreds({clientId:"",secretKey:""});setPage("credentials");
  }

  if(page==="credentials")return <CredentialsPage onNext={handleCredsNext}/>;
  if(page==="login")return <LoginPage clientId={creds.clientId} secretKey={creds.secretKey} onAuth={handleAuth} onBack={()=>setPage("credentials")}/>;

  const uid  =userData?.uid||userData?.USERID||userData?.susertoken||creds.clientId||"";
  const actid=userData?.actid||uid;

  const tabs=[{id:"copy",label:"Copy Trading"},{id:"orders",label:"Orders"},{id:"trades",label:"Trades"},{id:"positions",label:"Positions"},{id:"holdings",label:"Holdings"},{id:"funds",label:"Funds"},{id:"basket",label:"Basket Order"},{id:"slice",label:"Order Slicer"}];

  // Account switcher — all accounts (master + children) for viewing data
  const allAccounts = [
    { label: "My Account", uid, actid, token: authToken, isSelf: true },
    ...ctMasters
      .filter(m => m.uid && m.uid !== uid)
      .map(m => ({ label: m.label||m.uid, uid: m.uid, actid: m.actid,
        token: m.access_token==="__SESSION__" ? authToken : m.access_token, isMaster: true })),
    ...ctChildren
      .filter(c => c.uid)
      .map(c => ({ label: c.label||c.uid, uid: c.uid, actid: c.actid, token: c.access_token, isChild: true })),
  ];
  const safeIdx = Math.min(selectedAccIdx, Math.max(0, allAccounts.length - 1));
  const selAcc = allAccounts[safeIdx] || allAccounts[0] || { label:"My Account", uid, actid, token: authToken, isSelf: true };

  return (
    <div style={{minHeight:"100vh",background:C.bg,color:C.text,fontFamily:"'Inter',system-ui,sans-serif"}}>
      {/* Topbar */}
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"0 20px",display:"flex",alignItems:"center",gap:16,height:56,position:"sticky",top:0,zIndex:50}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}><Logo size={28}/><span style={{fontWeight:800,fontSize:15}}>Kambala Copy Trader</span></div>
          {ctRunning&&<div style={{display:"flex",alignItems:"center",gap:6,background:`${C.green}18`,border:`1px solid ${C.green}44`,borderRadius:20,padding:"3px 10px"}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:C.green,display:"inline-block"}}/>
            <span style={{fontSize:11,fontWeight:700,color:C.green}}>Engine Running</span>
          </div>}
        </div>
        <div style={{display:"flex",gap:2,flex:1,overflowX:"auto"}}>
          {tabs.map(t=><button key={t.id} onClick={()=>setActiveTab(t.id)} style={{background:activeTab===t.id?`${C.accent}22`:"transparent",border:"none",color:activeTab===t.id?C.accent:C.textDim,padding:"6px 14px",borderRadius:6,cursor:"pointer",fontSize:13,fontWeight:activeTab===t.id?700:500,whiteSpace:"nowrap"}}>{t.label}</button>)}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button style={{...btn("primary"),padding:"6px 14px"}} onClick={()=>setShowOrder(true)}>+ Place Order</button>
          <div style={{textAlign:"right"}}><div style={{fontSize:13,fontWeight:600,color:C.text}}>{uid}</div><div style={{fontSize:11,color:C.textDim}}>{creds.clientId}</div></div>
          <button style={{...btn("danger"),padding:"6px 12px"}} onClick={handleLogout}>Logout</button>
        </div>
      </div>
      {/* Content */}
      <div style={{maxWidth:1300,margin:"0 auto",padding:"24px 20px"}}>

        {/* Account Switcher — shown for all tabs except Copy Trading */}
        {activeTab!=="copy" && allAccounts.length > 1 && (
          <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <div style={{fontSize:12,fontWeight:700,color:C.textDim,textTransform:"uppercase",letterSpacing:"0.5px",flexShrink:0}}>Viewing:</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",flex:1}}>
              {allAccounts.map((acc,i)=>(
                <button key={i} onClick={()=>setSelectedAccIdx(i)} style={{
                  padding:"5px 14px",borderRadius:20,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s",
                  background: safeIdx===i
                    ? (acc.isSelf||acc.isMaster ? C.yellow : C.accent)
                    : `${C.border}`,
                  color: safeIdx===i ? "#000" : C.textDim,
                }}>
                  {acc.isSelf||acc.isMaster ? "👑" : "👶"} {acc.label}
                </button>
              ))}
            </div>
            <div style={{fontSize:11,color:C.textMuted}}>uid: <span style={{color:C.accent}}>{selAcc?.uid}</span></div>
          </div>
        )}

        {activeTab==="copy"&&<CopyTradingTab masters={ctMasters} setMasters={setCtMasters} children={ctChildren} setChildren={setCtChildren} rules={ctRules} setRules={setCtRules} running={ctRunning} log={ctLog} addLog={ctAddLog} onStart={ctStart} onStop={ctStop} onSync={ctSync} loggedUid={uid} loggedActid={actid}/>}
        {activeTab==="orders"&&<OrderBookTab key={selAcc?.uid} uid={selAcc?.uid} actid={selAcc?.actid} token={selAcc?.token}/>}
        {activeTab==="basket"&&<BasketOrderTab uid={uid} actid={actid} token={authToken} userDetails={userDetails}/>}
        {activeTab==="slice"&&<OrderSlicerTab uid={uid} actid={actid} token={authToken} userDetails={userDetails}/>}
        {activeTab==="trades"&&<TradeBookTab key={selAcc?.uid} uid={selAcc?.uid} actid={selAcc?.actid} token={selAcc?.token}/>}
        {activeTab==="positions"&&<PositionsTab key={selAcc?.uid} uid={selAcc?.uid} actid={selAcc?.actid} token={selAcc?.token}/>}
        {activeTab==="holdings"&&<HoldingsTab key={selAcc?.uid} uid={selAcc?.uid} actid={selAcc?.actid} token={selAcc?.token}/>}
        {activeTab==="funds"&&<FundsTab key={selAcc?.uid} uid={selAcc?.uid} actid={selAcc?.actid} token={selAcc?.token}/>}
      </div>
      {showOrder&&<PlaceOrderModal uid={uid} actid={actid} token={authToken} userDetails={userDetails} onClose={()=>setShowOrder(false)}/>}
    </div>
  );
}
